import type { ComplianceConsoleKind } from '../constants/complianceChecklists';

export type CatalogItemEs = {
  title: string;
  action: string;
  actionSteps?: string[];
  waitingSteps?: string[];
  verifySteps?: Record<string, { text: string; evidenceHint: string }>;
  completeLabel?: string;
  evidenceHint: string;
  dependencyNote?: string;
  docsLabel?: string;
  sourceLabel?: string;
};

export type CatalogEsOverlay = {
  title: string;
  subtitle: string;
  notices: Record<string, { title: string; body: string }>;
  pillars: Record<string, {
    label: string;
    summary: string;
    items: Record<string, CatalogItemEs>;
  }>;
};

const item = (
  title: string,
  action: string,
  evidenceHint: string,
  extra: {
    dependencyNote?: string;
    docsLabel?: string;
    sourceLabel?: string;
    actionSteps?: string[];
    waitingSteps?: string[];
    verifySteps?: Record<string, { text: string; evidenceHint: string }>;
    completeLabel?: string;
  } = {},
): CatalogItemEs => ({ title, action, evidenceHint, ...extra });

export const COMPLIANCE_CATALOG_ES: Record<ComplianceConsoleKind, CatalogEsOverlay> = {
  'ai-compliance': {
    title: 'Cumplimiento de IA',
    subtitle:
      'Un inventario, una regla de uso escrita y un dueño con nombre para las herramientas de IA que la empresa ya usa. CompliCore guarda el inventario y la regla. CompliAdvantage añade los términos del proveedor, la revisión humana y una revisión trimestral. CompliComplete añade una nota de impacto cuando un sistema afecta a una persona. Esto no es un certificado ISO/IEC 42001 ni una puntuación del NIST AI RMF.',
    notices: {
      'not-a-certificate': {
        title: 'Un registro, no un certificado',
        body: 'El NIST AI RMF 1.0 es una guía voluntaria: gobernar, mapear, medir y gestionar. ISO/IEC 42001 es una norma de sistema de gestión que una empresa puede buscar después. La Ley de IA de la Unión Europea es ley para algunos usos cuyo resultado se usa en la Unión Europea. Esta consola guarda el inventario, la regla y el dueño. NextCompli no certifica ninguno de esos puntos y no decide que una herramienta esté permitida.',
      },
      'ai-plan-ladder': {
        title: 'La cobertura se profundiza por suscripción',
        body: 'CompliCore es el inventario, la regla de uso de una página, los datos que nunca van a un modelo público y un dueño con nombre. CompliAdvantage añade los términos del proveedor, una persona que revisa el resultado antes de una decisión, una forma de apagar el sistema y una revisión trimestral. CompliComplete añade una nota de impacto para los sistemas que afectan a una persona, más las preguntas de alcance federal y de la Unión Europea. Ningún plan marca a la empresa como certificada.',
      },
    },
    pillars: {
      'ai-inventory-rule': {
        label: 'Inventario y regla de uso',
        summary: 'Liste cada herramienta de IA, nombre a quien guarda la lista y escriba qué datos nunca van a un modelo público.',
        items: {
          'ai-compliance-calendar': item(
            'Ponga las fechas de este trabajo en el calendario',
            'Abra el calendario de esta página y guarde una fecha para cada fila que aplique. Elija No este año solo cuando esa fila no aplique. No invente una fecha.',
            'Cada fecha que aplica, guardada en el calendario',
            {
              completeLabel: 'Marcar completo (fechas en el calendario)',
              verifySteps: {
                'ai-inventory-review': {
                  text: 'Guarde la fecha de la revisión del inventario de herramientas de IA, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
              },
            },
          ),
          'ai-inventory': item(
            'Inventario de herramientas de IA',
            'Liste cada herramienta de IA, incluidas las funciones de IA dentro del software que ya posee. Para cada una, escriba qué redacta o decide, qué datos puede ver, de quién es la cuenta y el proveedor. El NIST AI RMF pide este inventario. No pegue texto de clientes, indicaciones ni la salida del modelo en esta fila. Basta un nombre y un enlace.',
            'Inventario con herramienta, cuenta del dueño, datos que puede ver y proveedor',
          ),
          'ai-owner': item(
            'Dueño con nombre',
            'Nombre a una persona que mantiene el inventario y la regla de uso. Una empresa pequeña puede nombrar a la misma persona que ya cuida la seguridad. Escriba cómo el equipo avisa de una herramienta nueva antes de que alguien empiece a usarla.',
            'Nombre del dueño y la fecha en que se avisó al equipo',
            { dependencyNote: 'Empiece el inventario para que el dueño tenga una lista que cuidar.' },
          ),
          'ai-acceptable-use': item(
            'Regla de uso aceptable de una página',
            'Escriba qué herramientas están permitidas y qué datos nunca van a un modelo externo: datos personales de clientes, expedientes de salud, tarjetas de pago, archivos de contratos del gobierno y contraseñas. Diga cuándo una persona debe leer el resultado antes de usarlo. Mantenga la regla en una página.',
            'Página de la regla de uso con fecha',
            { dependencyNote: 'Nombre al dueño antes de publicar la regla que esa persona tiene que cuidar.' },
          ),
        },
      },
      'ai-vendor-review': {
        label: 'Revisión del proveedor y revisión humana',
        summary: 'En CompliAdvantage, conserve los términos del proveedor, una revisión humana antes de una decisión y una forma de detener un sistema que falla.',
        items: {
          'ai-vendor-terms': item(
            'Términos del proveedor para herramientas que ven datos privados',
            'Para cada herramienta que pueda ver datos de clientes o empleados, conserve la página del proveedor que dice si entrena con esos datos, cuánto tiempo los guarda y con quién los comparte. Si el proveedor no puede decirlo, no ponga datos privados en esa herramienta.',
            'Página o cláusula del contrato del proveedor, con fecha',
            { dependencyNote: 'El inventario nombra a los proveedores que esta revisión tiene que cubrir.' },
          ),
          'ai-human-review': item(
            'Una persona revisa el resultado antes de una decisión',
            'Una persona lee el resultado antes de usarlo para una decisión sobre una persona, un pago, una contratación o un cliente. Guarde un registro corto: la herramienta, la fecha y quién revisó. No guarde el texto privado que se revisó.',
            'Registro de revisión con herramienta, fecha y revisor — sin el texto privado de la indicación',
            { dependencyNote: 'La regla de uso dice cuándo una persona tiene que revisar. Esta fila es el registro.' },
          ),
          'ai-tell-people': item(
            'Diga cuándo una persona trata con un sistema de IA',
            'Cuando un cliente, un estudiante o un empleado trata con un sistema de IA, dígalo en lenguaje claro en esa pantalla o mensaje. No afirme que se cumple la Ley de IA de la Unión Europea. Esta fila solo registra que se avisó a las personas.',
            'Captura o enlace de la página que avisa a la persona',
          ),
          'ai-stop-path': item(
            'Una forma de detener un sistema que está mal',
            'Escriba quién puede apagar la herramienta y a quién avisar si el resultado es incorrecto o dañino. La función Gestionar del NIST AI RMF cubre detener un sistema que no hace lo que debía. Pruebe la ruta una vez y escriba la fecha.',
            'Nota de la ruta de parada y la fecha en que se probó',
            { dependencyNote: 'El dueño es la persona a la que tiene que llegar la ruta de parada.' },
          ),
          'ai-quarterly-review': item(
            'Revisión trimestral del inventario',
            'Cada trimestre, agregue las herramientas que el equipo empezó y quite las que dejó de usar. El dueño firma la fecha. No trate la lista del trimestre pasado como si fuera la actual.',
            'Fecha de revisión e iniciales del dueño',
            { dependencyNote: 'Revise el inventario que el dueño ya mantiene.' },
          ),
        },
      },
      'ai-impact': {
        label: 'Impacto y alcance',
        summary: 'En CompliComplete, escriba qué pasa si un sistema que afecta a una persona se equivoca, y si una regla federal o de la Unión Europea siquiera aplica.',
        items: {
          'ai-impact-note': item(
            'Nota de impacto para sistemas que afectan a una persona',
            'Para cada herramienta que pueda afectar a un cliente, empleado, estudiante o pago, escriba qué pasa si se equivoca, quién la revisa y cómo una persona puede pedir una segunda mirada. Media página basta. Esto no es un certificado ISO/IEC 42001.',
            'Nota de impacto por cada herramienta que afecta a una persona',
            { dependencyNote: 'El registro de revisión humana muestra qué herramientas necesitan una nota de impacto.' },
          ),
          'ai-federal-scope': item(
            'Uso de una agencia federal, si lo hay',
            'Si una agencia federal va a usar el sistema, pregunte si el inventario de IA de esa agencia bajo el Memorando M-24-10 de la OMB aplica a este trabajo. Ese memorando obliga a las agencias. No obliga por sí solo a todo contratista. No marque esto como terminado solo porque existe una herramienta comercial. Si no hay uso de agencia en alcance, archive una nota fechada que lo diga.',
            'Nota de uso de agencia, o una nota fechada de que no hay uso federal en alcance',
          ),
          'ai-eu-scope': item(
            'Resultado en la Unión Europea, si lo hay',
            'Si el resultado se usa en la Unión Europea, escriba si la empresa provee el sistema o solo usa la herramienta de un proveedor. Una herramienta interna de borrador solo en EE. UU. no está automáticamente en alcance. No afirme que se cumple la Ley de IA de la Unión Europea.',
            'Nota de alcance: en la Unión, o no',
          ),
          'ai-defense-use': item(
            'El uso de defensa se queda en Cumplimiento gubernamental',
            'Si el trabajo es para el Departamento de Defensa, manténgalo junto a los principios de IA responsable del departamento: responsable, equitativa, rastreable, confiable y gobernable. Eso no sustituye a CMMC, y no sustituye los derechos de datos de SBIR o STTR. Abra Cumplimiento gubernamental para esos registros. No marque esto como terminado solo por un chat comercial.',
            'Nota del uso de defensa, o una nota fechada de que no lo hay',
            { docsLabel: 'Abrir Cumplimiento gubernamental' },
          ),
        },
      },
      'ai-agent-security': {
        label: 'Seguridad de agentes',
        summary: 'Cuando una herramienta puede leer texto de afuera y luego actuar, conserve un registro de las barreras. Esto no es un certificado ISO/IEC 42001 ni una puntuación.',
        items: {
          'ai-agent-inventory': item(
            'Liste las herramientas que pueden actuar, no solo las que redactan',
            'En el inventario, marque cuáles pueden llamar a otros sistemas, enviar un mensaje, cambiar un archivo o gastar dinero. Un cuadro de chat que solo redacta texto no es lo mismo que una herramienta que actúa. Nombre a la persona que puede apagar cada herramienta que actúa.',
            'Filas del inventario marcadas solo-borrador o puede-actuar, con un dueño',
            { dependencyNote: 'Empiece el inventario antes de marcar qué herramientas pueden actuar.' },
          ),
          'ai-untrusted-content': item(
            'El texto de afuera no puede dar las órdenes a la herramienta',
            'Trate las páginas web, el correo, los adjuntos y los documentos recuperados como no confiables. No deben anular las instrucciones del sistema ni iniciar una acción por sí solos. Una regla aparte decide si la herramienta puede actuar. Registre los productos donde esta separación está activa y aquellos donde no.',
            'Nota de dónde se impide que el texto no confiable dé órdenes',
            { dependencyNote: 'Sepa qué herramientas pueden actuar antes de probar si el texto de afuera puede dirigirlas.' },
          ),
          'ai-input-prefilter': item(
            'Detenga prompts tóxicos, políticos y fuera de tema antes del modelo',
            'Para cada chat o asistente que la gente use, ejecute un filtro de entrada antes del LLM principal. Bloquee lenguaje tóxico, pedidos políticos u otros fuera de su lista escrita de temas, y frases comunes de inyección de prompts. Opciones de código abierto incluyen rieles de entrada de NVIDIA NeMo Guardrails con Meta Llama Guard (categorías de seguridad) y modelos de control de tema; equivalentes hospedados sirven. La prueba orientada al sitio también vive en Cumplimiento del sitio web → IA/ML Zero Trust y barreras. No pegue prompts privados en esta fila.',
            'Nombre del producto filtro + puntero a la lista de temas + una muestra bloqueada anonimizada',
            {
              dependencyNote: 'Liste las herramientas antes de probar qué filtro va delante de ellas.',
              docsLabel: 'Barreras IA/ML del sitio web',
              sourceLabel: 'Rieles de protección NVIDIA NeMo',
            },
          ),
          'ai-tool-least-privilege': item(
            'Cada herramienta que actúa recibe solo el acceso que necesita',
            'Liste las cuentas, los archivos y las acciones que cada herramienta que actúa puede alcanzar. Quite el acceso amplio. No ponga una contraseña de larga duración en la herramienta. Una persona aprueba una acción que no se puede deshacer, como un pago, una presentación o un mensaje a un cliente.',
            'Lista de acceso por herramienta y las acciones que esperan a una persona',
            { dependencyNote: 'La lista de herramientas que actúan va primero.' },
          ),
          'ai-output-check': item(
            'Revise lo que la herramienta envía',
            'Antes de usar la salida, revísela por datos privados, afirmaciones inventadas presentadas como hechos e instrucciones que empujarían a una persona u otra herramienta a hacer algo dañino. Registre quién revisa esa salida, o qué filtro del producto lo hace.',
            'Nombre del revisor o del filtro, y un ejemplo de una salida bloqueada',
            { dependencyNote: 'El paso de revisión humana muestra qué salidas necesitan esta revisión.' },
          ),
          'ai-supply-and-data': item(
            'Conozca el modelo y los documentos en los que confía',
            'Escriba qué modelo o proveedor usa cada herramienta que actúa, y qué conjunto de documentos busca. No deje que un archivo desconocido se vuelva una fuente de confianza. Si el modelo de un proveedor cambia, anote la fecha. Esta es la revisión de suministro y la revisión de envenenamiento. No es una prueba de laboratorio.',
            'Nombre del modelo o proveedor, fuente de documentos y la fecha de la última revisión',
            { dependencyNote: 'Los términos del proveedor van antes de confiar en el modelo de ese proveedor.' },
          ),
          'ai-red-team-and-rollback': item(
            'Intente un uso indebido y conserve una forma de deshacerlo',
            'Una vez al año, o antes de que una herramienta empiece a actuar sobre datos de clientes, intente dirigirla con una página hostil, un correo hostil y una petición que debería rechazar. Escriba qué pasó. Confirme que puede apagar la herramienta y deshacer la última acción. ai-stop-path es el interruptor. Esta fila es la prueba y el deshacer.',
            'Nota de prueba con fecha y el deshacer o el interruptor que usó',
            { dependencyNote: 'El interruptor y la regla del texto no confiable van antes de la prueba de uso indebido.' },
          ),
        },
      },
    },
  },
  'dod-contracts-compliance': {
    title: 'Cumplimiento gubernamental',
    subtitle:
      'Trabajo de proveedores federal, estatal, de condado y de ciudad en un solo expediente. Clasifique la empresa, el proyecto y el contrato. SOC 2, CMMC y una lista de GSA comparten un expediente de evidencia. Ninguno sustituye a los otros. CompliCore cubre FCI / CMMC Nivel 1 más las listas de proveedores que necesita una consultoría TI de Texas. CompliAdvantage añade el rastreador de Nivel 2 y el playbook DFARS 7012 sin almacenar CUI. CompliComplete añade SSP/POA&M, punteros GCC High/PreVeil y un enclave CUI opcional. No es un motor de certificación C3PAO.',
    notices: {
      'cmmc-phase-ii-pause': {
        title: 'Las evaluaciones de terceros de CMMC Fase II están en pausa',
        body: 'Desde el 13 de julio de 2026, el DoD suspendió los requisitos de CMMC Fase II (C3PAO) y hitos posteriores mientras se revisa el programa. Las autoevaluaciones de Fase I siguen vigentes cuando la solicitud las incluye. Lea cada aviso para el nivel de DFARS 252.204-7021. DFARS 252.204-7012 sigue aplicando si manejará CUI.',
      },
      'taxonomy-levels': {
        title: 'Clasifique empresa, proyecto y contrato por separado',
        body: 'La preparación de la empresa, el manejo de datos del proyecto y las cláusulas del contrato pueden diferir. Use el banco de niveles: el Nivel 1 queda en 15 prácticas FAR más la afirmación SPRS. Desbloquee SPRS numérico, SSP/POA&M, 110 filas NIST 800-171, punteros GCC High/Entra/Intune y un buzón CUI de PreVeil solo cuando haya CUI en alcance.',
      },
      'fci-first': {
        title: 'Los primeros premios de consultoría TI suelen ser FCI, no clasificados',
        body: 'La mayoría de la consultoría TI DoD para pequeñas empresas empieza en trabajo no clasificado. Proteja la Información de Contratos Federales bajo FAR 52.204-21 (CMMC Nivel 1, 15 prácticas, afirmación SPRS anual, sin POA&M). No oferte trabajo CUI o clasificado hasta que NIST 800-171 / DFARS 252.204-7012 y cualquier ruta de habilitación estén realmente en su lugar.',
      },
      'plan-coverage-ladder': {
        title: 'La cobertura se profundiza por suscripción',
        body: 'CompliCore es FCI / CMMC Nivel 1: entidad, SAM, seguro, las 15 prácticas FAR 52.204-21 y la afirmación SPRS. No cargue CUI. CompliAdvantage añade el rastreador de 110 prácticas de Nivel 2, el espacio POA&M y el playbook de incidentes de 72 horas DFARS 252.204-7012 — la CUI permanece en el enclave del cliente. CompliComplete añade exportación SSP, punteros GCC High/Entra/Intune, un puntero de buzón CUI PreVeil y un enclave FedRAMP Moderate opcional. NextCompli nunca presenta SPRS ni certifica al cliente.',
      },
      'data-impact-bar': {
        title: 'Ajuste el nivel de nube a los datos, no a CMMC',
        body: 'Si una agencia guardará registros en un producto en línea, elija el nivel según los datos. Bajo (público o no confidencial; una filtración hace daño limitado) es TX-RAMP Nivel 1 o FedRAMP Low: nombres de inicio de sesión y páginas públicas caben aquí; los registros de agencia no. Normal (registros confidenciales del día a día; una filtración hace daño serio, no pérdida de vida) es TX-RAMP Nivel 2 o FedRAMP Moderate: aquí está la mayoría del software de agencias. Alto (orden público, seguridad nacional o una filtración catastrófica) es FedRAMP High; Texas sigue usando TX-RAMP Nivel 2 y luego añade controles extra. La consultoría de mano de obra que no aloja registros de agencia suele ser CMMC / FCI, no TX-RAMP. Las ciudades y los distritos escolares suelen pedir SOC 2 en vez de una autorización de nube. La reciprocidad de FedRAMP o GovRAMP no es automática después del 30 de oct. de 2024: pídala en SPECTRIM de DIR.',
      },
      'vendor-lists-layers': {
        title: 'Las listas de proveedores locales, de condado, estatales y federales son distintas',
        body: 'Cada comprador lleva su propio expediente de proveedor. SAM.gov (UEI/CAGE) es la puerta federal de adjudicación y debe renovarse cada 365 días. Texas usa el Boletín Electrónico de Negocios del Estado para publicar trabajo y la Lista Maestra Centralizada de Postores (70 USD al año, exige EIN) para enviar avisos: el CMBL no es obligatorio para ofertar y no es un contrato DIR. Los contratos TI de DIR se ganan respondiendo un RFO, no inscribiéndose. Regístrese en los portales de ciudad y condado de los lugares que vaya a servir. San Antonio usa SAePS para bienes y servicios profesionales (CIVCAST es construcción/A&E). Algunas ciudades solo publican un tablero de finanzas y no envían avisos. Los distritos escolares usan Bonfire, IonWave, PlanetBids o similar. Cooperativas como BuyBoard, TIPS y HGACBuy son catálogos opcionales que se ganan: no invente un número de contrato. Los códigos de clase/ítem NIGP impulsan avisos de ciudad, condado y CMBL; SAM usa NAICS. Regístrese una vez por portal. Guarde las contraseñas en su administrador de contraseñas, no aquí. Ninguna de estas listas sustituye SAM, y SAM no lo registra con la ciudad.',
      },
      'shared-assurance': {
        title: 'SOC 2, CMMC y GSA son tres programas',
        body: 'Mantenga una biblioteca de controles y una carpeta de evidencia. Un informe SOC 2 Tipo II muestra que un despacho de CPA probó los Criterios de Servicios de Confianza durante un período. No crea un estatus CMMC, una puntuación SPRS ni una autorización FedRAMP. CMMC Nivel 2 son los 110 requisitos de NIST SP 800-171. Mapee ambos a través de NIST SP 800-53 (mapa del AICPA y luego el Apéndice D de NIST SP 800-171) y cierre las brechas que cada comprador aún exige. Una Lista de Adjudicación Múltiple de GSA es un contrato que se gana cuando SAM está Active. No es una certificación cibernética. El DoD sigue esperando NIST SP 800-171 cuando el contrato cubre CUI, incluso mientras las evaluaciones de CMMC Fase II están en pausa.',
      },
      'sbir-sttr-phases': {
        title: 'SBIR y STTR tienen tres fases, y la Fase III no es dinero reservado',
        body: 'El Fondo Semilla de América (SBIR y STTR) se reautorizó el 13 de abril de 2026 hasta el 30 de septiembre de 2031. La Fase I prueba la idea, por lo general en 6 a 12 meses. La Fase II construye el prototipo, por lo general hasta dos años. La Fase III vende o pone en uso el resultado con capital privado u otro contrato federal. La Fase III no se paga con fondos SBIR o STTR, y no tiene tope de dinero, tiempo ni cantidad. Desde abril de 2026 una agencia puede adjudicar una Fase I hasta 323,090 USD y una Fase II hasta 2,153,927 USD, incluidas las modificaciones, sin pedir una exención a la SBA. Un tema de defensa puede fijar un tope menor. Las propuestas de defensa van por DSIP. NextCompli no presenta la propuesta y no inventa un número de adjudicación.',
      },
      'insurance-far-stack': {
        title: 'Ajuste el seguro al aviso, no a una lista de compras',
        body: 'FAR 28.307-2 nombra compensación laboral, CGL y responsabilidad de automóvil como los pisos habituales del contrato. La cobertura de aeronave y de buque aplica solo si el trabajo las usa: no para consultoría TI de escritorio. E&O, ciber, paraguas, crimen, EPLI, D&O y una BOP son extras: cómprelos cuando un aviso, un prime o una contratación cree el riesgo. Las fianzas Miller Act son para construcción, no para servicios TI ordinarios. No invente un número de póliza. No pague hasta que la cuenta corriente de la LLC se quede en o por encima del piso operativo después de las primas. El auto personal y el seguro de salud no son sustitutos. El impuesto de desempleo no es una póliza de compensación laboral.',
      },
    },
    pillars: {
      'entity-eligibility': {
        label: 'Elegibilidad de la entidad',
        summary: 'Entidad legal, identificadores, SAM.gov, banca, seguros, W-9, estatus de cuenta de impuesto de franquicia de Texas y certificaciones socioeconómicas. Los portales de ciudad, condado y estado van junto a SAM; no lo sustituyen.',
        items: {
          'tx-llc-good-standing': item(
            'Entidad legal en regla',
            'Confirme que el oferente es la LLC constituida (no un nombre personal ni un DBA no presentado), mantenga al día el SOS y el agente registrado, y use el nombre legal de la carta EIN en SAM, el banco y las facturas.',
            'Certificado de constitución + número de expediente SOS',
            { docsLabel: 'SOS de Texas — corporaciones' },
          ),
          'tx-franchise-account-status': item(
            'Estatus de cuenta de impuesto de franquicia de Texas',
            'Busque la LLC en la búsqueda de estatus de cuenta de impuesto de franquicia del Contralor. Texas ya no llama a esto un certificado de good standing. El certificado de constitución del SOS no es el mismo documento. El estado y muchos compradores locales esperan que el estatus de cuenta muestre el derecho a realizar negocios en Texas. No invente un número de certificado.',
            'Impresión o captura del estatus de cuenta del Contralor (sin certificado inventado)',
            {
              completeLabel: 'Marcar completo (estatus de cuenta en archivo)',
              dependencyNote: 'Necesita la LLC constituida. Independiente de SAM.gov.',
              docsLabel: 'Búsqueda de estatus de cuenta',
              sourceLabel: 'Estatus de cuenta de impuesto de franquicia',
              verifySteps: {
                'given-identity': {
                  text: 'Confirme que el nombre legal y el EIN están en el proyecto (Hazlo por mí los usa automáticamente).',
                  evidenceHint: 'Nombre legal + EIN en el proyecto',
                },
                'when-lookup': {
                  text: 'NextCompli busca el estatus de cuenta de impuesto de franquicia (API pública o bot de búsqueda COA pública — sin inicio de sesión del contribuyente). No es el certificado de constitución; no invente un número de certificado.',
                  evidenceHint: 'Resultado right-to-transact del Contralor o impresión',
                },
                'then-vault': {
                  text: 'Guarde la impresión o captura del estatus de cuenta en la bóveda de evidencia para el expediente del contrato (se prepara sola si la búsqueda tiene éxito).',
                  evidenceHint: 'Impresión o captura del estatus de cuenta del Contralor (sin certificado inventado)',
                },
              },
            },
          ),
          'compliance-calendar-dates': item(
            'Poner los eventos de cumplimiento en el calendario',
            'Abra el calendario de cumplimiento y guarde una fecha para cada evento que aplique este año: la renovación de SAM, el reporte de franquicia de Texas, la afirmación de CMMC y el certificado de seguro. Elija No este año solo cuando ese evento no aplique. No invente una fecha.',
            'Cada evento aplicable guardado en el calendario de cumplimiento',
            {
              completeLabel: 'Marcar completo (eventos en el calendario)',
              dependencyNote: 'Necesita la empresa constituida. Las fechas salen de los registros, no de una suposición.',
              verifySteps: {
                'sam-renewal': {
                  text: 'Guarde la fecha de vencimiento de SAM.gov, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
                'tx-franchise-report': {
                  text: 'Guarde la fecha del reporte de franquicia de Texas, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
                'cmmc-affirmation': {
                  text: 'Guarde la fecha de afirmación de CMMC, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
                'insurance-certificate': {
                  text: 'Guarde la fecha del certificado de seguro, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
              },
            },
          ),
          'ein-irs': item(
            'EIN del IRS emitido y usable',
            'Registre el EIN, el name control y la parte responsable. El banco y SAM pueden usar el EIN de inmediato; espere la ventana de coincidencia TIN del IRS antes de presentar electrónicos.',
            'Carta CP575 / confirmación de EIN',
            { dependencyNote: 'Necesita la LLC constituida. El banco y SAM usan este EIN.', docsLabel: 'Documentación EIN del IRS', sourceLabel: 'Solicitar un EIN' },
          ),
          'business-bank': item(
            'Cuenta corriente a nombre de la LLC',
            'Vaya a un banco y abra una cuenta corriente a nombre legal de la LLC. Ponga el EIN en la solicitud. SAM.gov no puede pagar a la empresa sin esta cuenta.',
            'Título de la cuenta + últimos cuatro + fecha de apertura',
            {
              dependencyNote: 'Necesita primero la LLC y el EIN. No espera a SAM.gov. SAM se puede empezar sin esta cuenta; SAM no puede pagar a la empresa (EFT) hasta que esta cuenta esté en SAM.',
              docsLabel: 'SBA: Abrir una cuenta bancaria empresarial',
            },
          ),
          'w9-on-file': item(
            'W-9 listo para pagadores gubernamentales',
            'Conserve un Formulario W-9 del IRS firmado con el nombre legal de la LLC y el EIN. Los condados y las ciudades lo piden antes de pagar. No escriba un número de Seguro Social en esta consola. Entregue el W-9 al comprador, no en NextCompli.',
            'Nota de que existe un W-9 firmado (no suba SSN)',
            {
              completeLabel: 'Marcar completo (W-9 listo)',
              dependencyNote: 'Necesita la LLC y el EIN. Independiente de SAM.gov.',
              sourceLabel: 'Formulario W-9 del IRS',
            },
          ),
          'sam-uei-cage': item(
            'UEI y CAGE de SAM.gov',
            'Confirme cada paso de SAM con prueba. Si SAM ya muestra Entity Under Review o Pending ID Assignment, espere el correo de GSA — no empiece un segundo registro. Use la cuenta Login.gov que ya tiene — no cree una segunda Login.gov para la empresa. Copie el UEI y el CAGE solo cuando SAM marque el registro Activo. No invente esos números. DUNS no es obligatorio.',
            'Captura del registro SAM Activo (UEI + CAGE)',
            {
              verifySteps: {
                'sam-sign-in': {
                  text: 'Abra sam.gov y pulse Sign In (arriba a la derecha). Use la cuenta Login.gov que ya tiene. Login.gov es un acceso personal, no una cuenta de la empresa.',
                  evidenceHint: 'Captura del Workspace después de Sign In',
                },
                'sam-register-all-awards': {
                  text: 'En Workspace pulse Get Started y luego Register Entity. Elija el propósito All Awards. No elija Unique Entity ID only — con eso no puede licitar como prime.',
                  evidenceHint: 'Captura de All Awards / Register Entity',
                },
                'sam-entity-details': {
                  text: 'Complete el nombre legal de la LLC, el EIN, la dirección física, el correo o agente registrado, el tipo LLC, la fecha de inicio en el SOS y el DBA si ya hay un nombre supuesto presentado.',
                  evidenceHint: 'Captura de Entity Information',
                },
                'sam-submit-docs': {
                  text: 'Envíe el registro y los documentos requeridos, luego pulse Check Entity Status. Conserve el número de incidente GSA FSD. El registro puede tardar hasta 10 días hábiles.',
                  evidenceHint: 'Pantalla Documentation Successfully Submitted o número INC-',
                },
                'sam-gsa-validation': {
                  text: 'Si SAM ya muestra Entity Under Review o Pending ID Assignment, espere el correo de GSA. No pulse Get Started otra vez y no empiece un segundo registro. Si GSA pide más archivos, use Open Cases en la misma entidad.',
                  evidenceHint: 'Correo de GSA / FSD o captura de Entity Under Review',
                },
                'sam-naics-889': {
                  text: 'Cuando GSA cierre el caso, ponga NAICS principal 541512. Agregue también 541511, 541519 y 541611. Termine los cuestionarios FAR, incluyendo 52.204-24 / 52.204-25 (Section 889).',
                  evidenceHint: 'Captura de NAICS y representaciones',
                },
                'sam-eft': {
                  text: 'Cuando SAM pida EFT, escriba los últimos cuatro de la cuenta corriente. SAM no puede pagarle a la empresa sin eso. No guarde aquí el routing ni el número de cuenta completo.',
                  evidenceHint: 'Página EFT con solo los últimos cuatro',
                },
                'sam-active-ids': {
                  text: 'Cuando el estado sea Active, copie el UEI y el CAGE que SAM asignó. No escriba identificadores inventados.',
                  evidenceHint: 'Captura del registro SAM Activo (UEI + CAGE)',
                },
              },
              actionSteps: [
                'Si SAM ya muestra Entity Under Review o Pending ID Assignment, párese. No pulse Get Started otra vez y no empiece un segundo registro.',
                'Abra sam.gov y pulse Sign In (arriba a la derecha). Use la cuenta Login.gov que ya tiene. Login.gov es un acceso personal, no una cuenta de la empresa.',
                'En Workspace pulse Get Started y luego Register Entity. No elija Unique Entity ID only — con eso no puede licitar como prime.',
                'Elija el propósito All Awards.',
                'Complete el nombre legal de la LLC, el EIN, la dirección física, el correo o agente registrado, el tipo LLC y la fecha de inicio en el SOS.',
                'Ponga NAICS principal 541512. Agregue también 541511, 541519 y 541611. Termine los cuestionarios FAR, incluyendo 52.204-24 / 52.204-25 (Section 889).',
                'Cuando SAM pida EFT, escriba los últimos cuatro de la cuenta corriente. SAM no puede pagarle a la empresa sin eso.',
                'Envíe y pulse Check Entity Status en la tarjeta de inicio de SAM. El registro puede tardar hasta 10 días hábiles.',
                'Cuando el estado sea Active, copie el UEI y el CAGE que SAM asignó. No escriba identificadores inventados.',
              ],
              waitingSteps: [
                'No pulse Get Started ni Register Entity otra vez mientras SAM muestre Entity Under Review o Pending ID Assignment.',
                'Vigile el correo de Login.gov para la respuesta de GSA / FSD (normalmente 1.5–3.5 días hábiles; fines de semana y feriados federales no cuentan).',
                'Si GSA pide más archivos, use Open Cases en la misma entidad. No abra un registro nuevo.',
                'Cuando el correo diga que la entidad fue validada, vuelva a Workspace, continúe NAICS, 889 y EFT, y copie UEI y CAGE solo cuando el estado sea Active.',
              ],
              completeLabel: 'Marcar completo (UEI y CAGE)',
              dependencyNote: 'Necesita la LLC y el EIN. Puede empezar SAM y obtener un UEI/CAGE sin la cuenta bancaria. Necesita el banco antes de que SAM pueda pagarle (EFT). Use la cuenta personal de Login.gov que ya tiene. No cree una segunda cuenta Login.gov para la empresa.',
              docsLabel: 'Guía de registro de entidades SAM.gov',
              sourceLabel: 'Abrir SAM.gov',
            },
          ),
          'naics-reps-certs': item(
            'NAICS, tamaño y representaciones',
            'En ese mismo registro de SAM.gov, ponga NAICS principal 541512 (diseño de sistemas informáticos). Agregue también 541511, 541519 y 541611. Complete los cuestionarios de tamaño y FAR que SAM pida.',
            'Fecha de NAICS + Reps & Certs en SAM',
            {
              dependencyNote: 'Hágalo en el mismo registro SAM.gov que el UEI/CAGE. Puede llenar NAICS y representaciones mientras SAM está en curso. Este paso no espera a la cuenta bancaria.',
              docsLabel: 'Búsqueda NAICS del Censo',
              sourceLabel: 'Abrir SAM.gov',
            },
          ),
          'psc-codes': item(
            'Códigos PSC en la página pública',
            'Elija los códigos de producto o servicio que describen cómo un comprador adquiriría el trabajo, y muéstrelos en texto público junto al nombre legal, el UEI, el CAGE y el NAICS. Para software a medida vendido como labor, el código habitual es DA01. Para ese mismo software vendido como suscripción, el código habitual es DA10. No copie los códigos de otra empresa. El PSC no se guarda en SAM como el NAICS. El comprador pone el PSC principal en la línea del contrato y en FPDS. Use el manual de PSC. No invente un código.',
            'Página pública o capability statement con los códigos PSC',
            {
              dependencyNote: 'Necesita primero la lista NAICS para que los PSC describan el mismo trabajo.',
              docsLabel: 'Guía de GSA para elegir un PSC',
              sourceLabel: 'Manual PSC (Acquisition.gov)',
            },
          ),
          'sam-renewal-365': item(
            'Renovación anual de SAM.gov',
            'Los registros de entidad en SAM.gov vencen cada 365 días. Calendario la fecha de renovación del registro Active. Un SAM vencido no puede recibir adjudicaciones federales. Renueve en la misma entidad: no inicie un segundo registro.',
            'Fecha de vencimiento de SAM en el calendario, o registro Active que muestre el próximo vencimiento',
            {
              completeLabel: 'Marcar completo (renovación fechada)',
              dependencyNote: 'Necesita un registro SAM Active. No inicie un segundo registro para renovar.',
              sourceLabel: 'Registro de entidades SAM.gov',
            },
          ),
          'sam-exclusions-self': item(
            'Exclusiones SAM / verificación de responsabilidad',
            'Cuando SAM esté Active, busque Exclusiones SAM (FAR 9.4) y el registro de Responsibility/Qualification de la entidad (antes FAPIIS) por el nombre legal de la LLC. No debería aparecer en la lista de partes excluidas. No invente un registro de exclusión. Vuelva a buscar después de un cambio de propiedad o de nombre.',
            'Búsqueda de exclusiones que muestre que el nombre legal de la LLC no está listado',
            {
              completeLabel: 'Marcar completo (no excluido)',
              dependencyNote: 'Necesita un registro SAM Active. No invente una exclusión ni un hallazgo FAPIIS.',
              sourceLabel: 'Exclusiones SAM.gov',
            },
          ),
          'tx-hub-certification': item(
            'Certificación HUB de Texas',
            'Solicite la certificación de Empresa Históricamente Subutilizada (HUB) en el Comptroller después de que existan la LLC y el EIN. No invente un número HUB. No marque completo hasta que el certificado del Comptroller esté en archivo. HUB ayuda en trabajo estatal y algo local; no sustituye SAM.gov.',
            'PDF del certificado HUB (Comptroller) — no invente el número',
            {
              completeLabel: 'Marcar completo (certificado HUB en archivo)',
              dependencyNote: 'Necesita la LLC constituida y el EIN. Independiente de SAM.gov. No invente un número HUB.',
              docsLabel: 'Guía de certificación HUB',
              sourceLabel: 'HUB del Comptroller de Texas',
              verifySteps: {
                'hub-eligibility': {
                  text: 'Confirme la propiedad y el control con sede en Texas contra las reglas HUB del Comptroller antes de solicitar. No reclame HUB en la declaración de capacidades hasta que exista el certificado.',
                  evidenceHint: 'Notas de elegibilidad (propiedad / control) — sin número de certificado inventado',
                },
                'hub-apply': {
                  text: 'Envíe la solicitud HUB en el portal CMBL / HUB del Comptroller con el nombre legal de la LLC y el EIN. Conserve el número de confirmación.',
                  evidenceHint: 'Confirmación del portal o ID de solicitud',
                },
                'hub-certificate': {
                  text: 'Cuando el Comptroller emita el certificado, archívelo y copie aquí el número HUB. No escriba un número adivinado.',
                  evidenceHint: 'PDF del certificado HUB o captura del Comptroller',
                },
              },
            },
          ),
          'sba-certifications': item(
            'Certificaciones SBA (8(a), WOSB, HUBZone, pequeña)',
            'Cuando SAM esté Active, confirme el estándar de tamaño de pequeña empresa en SAM y solicite solo las certificaciones SBA para las que la LLC realmente califique (8(a), WOSB/EDWOSB, HUBZone, SDVOSB). WOSB/EDWOSB es la reserva de empresas de mujeres cuando las mujeres poseen y controlan al menos el 51 por ciento. 8(a) suele exigir dos años de operación. Una empresa conjunta 8(a) es otra empresa. No reclame el 8(a) de un socio ni el vehículo de contrato de un socio. No invente certificaciones. No reclame 8(a) hasta que la SBA lo otorgue. La Búsqueda de Pequeñas Empresas de la SBA (SBS, antes DSBS) es un paso posterior cuando SAM esté Active.',
            'Certificado(s) SBA o N/A documentado — no invente el estatus del programa',
            {
              completeLabel: 'Marcar completo (certs SBA en archivo o N/A)',
              dependencyNote: 'Necesita LLC, EIN y un registro SAM Active antes de SBA Certify. No invente estatus 8(a), WOSB o HUBZone. No inicie un segundo registro SAM.',
              docsLabel: 'Guía de contratación federal de la SBA',
              sourceLabel: 'SBA Certify',
              verifySteps: {
                'sba-sam-size': {
                  text: 'Espere a que el estado de SAM.gov sea Active y luego confirme la representación de tamaño de pequeña empresa NAICS 541512 en SAM. No inicie un segundo registro SAM.',
                  evidenceHint: 'Captura de reps Active de SAM que muestre el tamaño de pequeña empresa',
                },
                'sba-eligibility': {
                  text: 'Evalúe 8(a), WOSB/EDWOSB, HUBZone y SDVOSB contra la propiedad, ubicación y tiempo en el negocio reales. 8(a) suele exigir dos años de operación. Una empresa conjunta 8(a) es otra empresa, con su propio registro SAM. No solicite un programa para el que la LLC no califique.',
                  evidenceHint: 'Notas de elegibilidad por programa (califica / aún no / N/A)',
                },
                'sba-certify': {
                  text: 'Si califica, presente en SBA Certify con el nombre legal de la LLC y el EIN. Conserve el ID de solicitud. No marque completo hasta que la SBA emita el certificado.',
                  evidenceHint: 'Confirmación de SBA Certify o ID de solicitud',
                },
                'sba-certificate': {
                  text: 'Archive cada certificado SBA. Copie solo números que la SBA emitió. Luego complete el paso separado del perfil de Búsqueda de Pequeñas Empresas de la SBA (SBS).',
                  evidenceHint: 'PDF del certificado SBA o captura de Certify',
                },
              },
            },
          ),
          'insurance-eo-cgl': item(
            'Seguros E&O y CGL',
            'Contrate responsabilidad profesional (E&O) y responsabilidad general comercial (CGL) a nombre legal de la LLC cuando las cotizaciones actuales ya estén en archivo. FAR 28.307-2 fija un piso de CGL de 500.000 USD por ocurrencia; el objetivo para licitar es 1 millón cada una. El E&O no es un mínimo FAR, pero los primes y los avisos de TI lo piden con frecuencia. Antes de ejecutar, conserve un certificado de seguro en el archivo como prueba (FAR 52.228-5, FAR 4.803, DAFFARS 5328.310). Un COI evidencia cobertura; no sustituye la póliza. No marque completo hasta que el COI esté en archivo.',
            'Certificado de seguro (asegurado nombrado = nombre legal de la LLC, 1 M USD E&O + 1 M USD CGL)',
            {
              verifySteps: {
                'ins-quote': {
                  text: 'Obtenga cotizaciones vigentes a nombre legal de la LLC por 1 millón de dólares de responsabilidad general comercial y 1 millón de responsabilidad profesional (E&O). La cotización CGL suele excluir E&O: contrate ambas. El mínimo CGL de FAR 28.307-2 es 500.000 USD; 1 millón es el objetivo para licitar. No busque un segundo mercado salvo que la cotización en archivo no se pueda contratar.',
                  evidenceHint: 'PDF de cotización o correo del corredor con asegurado nombrado y límites',
                },
                'ins-bind': {
                  text: 'Contrate ambas pólizas. El asegurado nombrado debe ser el nombre legal de la LLC, no solo el DBA. Conserve la carátula o las declaraciones.',
                  evidenceHint: 'Carátula o declaraciones con el nombre legal de esta empresa',
                },
                'ins-coi': {
                  text: 'Archive el certificado de seguro (ACORD 25 o equivalente) con asegurado nombrado, límites y fechas de vigencia. Los oficiales de contratación piden esto como prueba de seguro y lo guardan en el expediente. Suba el COI o anote aquí los números de póliza. No marque completo hasta que esta prueba esté en archivo.',
                  evidenceHint: 'PDF o captura del COI, o números de póliza y fechas de vigencia',
                },
              },
              completeLabel: 'Marcar completo (COI en archivo)',
              dependencyNote: 'Necesita el nombre legal de la LLC en la póliza y el COI. Independiente de SAM.gov y de la cuenta bancaria.',
              docsLabel: 'SBA: Obtener seguro empresarial',
            },
          ),
          'insurance-cyber': item(
            'Seguro de responsabilidad cibernética',
            'El seguro cibernético es distinto del E&O y del CGL. La cobertura de primera parte paga los costos del incidente de la LLC (forense, restauración, ransomware, interrupción, avisos). La de tercera parte paga si un cliente alega que el trabajo de la LLC causó su filtración. Un E&O de tecnología puede incluir algo de ciber de tercera parte; no sustituye los costos de primera parte en los sistemas de la LLC. FAR 28.307 y CMMC no exigen una póliza ciber, pero una solicitud o un subcontrato de prime a menudo sí. Anote el límite ciber que nombra el aviso. Muchos avisos piden unos 2 millones de dólares. Esa cifra es la del aviso, no un mínimo FAR. Comprar una póliza no cumple el reporte de 72 horas DFARS 252.204-7012, SPRS ni CMMC. No invente un número de póliza. No marque completo hasta que haya un COI o una declinación por escrito.',
            'COI ciber (asegurado nombrado = nombre legal de la LLC) o declinación por escrito',
            {
              completeLabel: 'Marcar completo (COI ciber o declinación en archivo)',
              dependencyNote: 'Pida el ciber después de que existan cotizaciones E&O y CGL. Independiente de SAM.gov. No invente una póliza.',
              docsLabel: 'SBA: Obtener seguro empresarial',
              sourceLabel: 'SBA: Obtener seguro empresarial',
              verifySteps: {
                'cyber-need': {
                  text: 'Decida si la LLC aloja datos de clientes o de agencias, o solo asesora en los sistemas del cliente. Alojar o procesar PII/FCI en inquilinos de la LLC es necesidad ciber de primera parte. Asesorar o implementar en la red de un cliente es principalmente tercera parte / E&O de tecnología. Si un aviso nombra un límite ciber, escriba ese número junto al límite de la cotización. Pregunte al corredor cuando las cotizaciones E&O y CGL estén vigentes.',
                  evidenceHint: 'Correo del corredor o notas de necesidad de primera frente a tercera parte',
                },
                'cyber-quote': {
                  text: 'Obtenga una cotización a nombre legal de la LLC. Las aseguradoras suelen preguntar por MFA, detección en endpoints, copias de seguridad y (en trabajo de defensa) el estatus CMMC/SPRS. Esas respuestas deben coincidir con la consola DoD: falsear CMMC en una solicitud puede anular la cobertura y es un riesgo de False Claims Act que la póliza casi nunca paga.',
                  evidenceHint: 'PDF de cotización o correo del corredor con asegurado nombrado y límites',
                },
                'cyber-bind-or-decline': {
                  text: 'Contrate cuando un contrato lo exija y el efectivo pueda quedarse sobre el piso operativo después de las primas, o archive una declinación por escrito hasta que un aviso lo pida. Archive el COI cuando esté contratado. No marque completo sin esa prueba o la nota de declinación.',
                  evidenceHint: 'PDF del COI, número de póliza y fechas, o declinación por escrito',
                },
              },
            },
          ),
          'insurance-workers-comp': item(
            'Compensación laboral y responsabilidad del empleador',
            'FAR 28.307-2 pide compensación laboral más responsabilidad del empleador de al menos 100.000 USD. Texas no exige compensación laboral a la mayoría de los empleadores privados, pero los avisos federales, de DIR y de ciudades a menudo sí. Los miembros que poseen 25 por ciento o más suelen poder excluirse. El seguro de salud no la sustituye. Si la LLC no tiene empleados W-2 y un aviso no lo pide, archive un N/A por escrito. No invente una póliza. No marque completo hasta que haya un COI o la nota N/A.',
            'COI de compensación laboral o N/A por escrito (asegurado nombrado = nombre legal de la LLC)',
            {
              completeLabel: 'Marcar completo (COI de compensación laboral o N/A en archivo)',
              dependencyNote: 'Pida después de que existan cotizaciones E&O y CGL. Independiente de SAM.gov. No invente una póliza.',
              docsLabel: 'DWC de Texas — cobertura',
              verifySteps: {
                'wc-need': {
                  text: 'Cuente empleados W-2 (no contratistas 1099). Si una solicitud o una cláusula DIR/ciudad exige compensación laboral, cómprela aunque Texas le permita omitirla. Pregunte al corredor cuando existan cotizaciones E&O y CGL.',
                  evidenceHint: 'Nota de plantilla o extracto de cláusula',
                },
                'wc-bind-or-na': {
                  text: 'Contrate compensación laboral estatutaria de Texas con responsabilidad del empleador de al menos 100.000 USD (objetivo 1 millón) a nombre legal de la LLC, o archive un N/A / exclusión de miembro hasta que un aviso lo pida. Archive el COI cuando esté contratado. No marque completo sin esa prueba.',
                  evidenceHint: 'COI de compensación laboral, elección de exclusión o N/A por escrito',
                },
              },
            },
          ),
          'insurance-auto': item(
            'Responsabilidad de automóvil / HNOA',
            'FAR 28.307-2 pide responsabilidad de automóvil cuando se usan autos en el contrato. Los pisos en EE.UU. son 200.000 USD por persona / 500.000 USD por ocurrencia de lesiones y 20.000 USD de daños a bienes; el objetivo para licitar es 1 millón de límite combinado. El auto personal no es un sustituto. Si la LLC no tiene vehículo, añada HNOA para autos personales o de renta usados en el trabajo. La responsabilidad de aeronave y de buque es N/A para consultoría TI de escritorio. No invente una póliza. No marque completo hasta que haya un COI o un N/A por escrito.',
            'COI de auto o HNOA (asegurado nombrado = nombre legal de la LLC) o N/A por escrito',
            {
              completeLabel: 'Marcar completo (COI de auto o N/A en archivo)',
              dependencyNote: 'Pida después de que existan cotizaciones E&O y CGL. Independiente de SAM.gov. No invente una póliza.',
              docsLabel: 'SBA: Obtener seguro empresarial',
              verifySteps: {
                'auto-need': {
                  text: 'Registre si alguien maneja a un sitio de cliente, una base o una oficina de ciudad para trabajo de la LLC. Si sí, entra HNOA o auto comercial. Si nadie maneja para la empresa, escriba N/A.',
                  evidenceHint: 'Nota de uso de manejo o N/A',
                },
                'auto-bind-or-na': {
                  text: 'Contrate HNOA o auto comercial a nombre legal de la LLC, o archive un N/A por escrito. Archive el COI cuando esté contratado. No marque completo sin esa prueba.',
                  evidenceHint: 'COI de auto/HNOA o N/A por escrito',
                },
              },
            },
          ),
          'insurance-umbrella': item(
            'Responsabilidad paraguas / exceso',
            'Un paraguas apila límites extra encima de CGL, auto y responsabilidad del empleador cuando un aviso quiere de 2 a 5 millones en vez de 1 millón. No se sienta sobre E&O, ciber, D&O ni EPLI: esos necesitan su propio exceso. No compre hasta que una solicitud o un prime lo pida. No invente una póliza. No marque completo hasta que haya un COI o una declinación por escrito.',
            'COI paraguas (asegurado nombrado = nombre legal de la LLC) o declinación por escrito',
            {
              completeLabel: 'Marcar completo (COI paraguas o declinación en archivo)',
              dependencyNote: 'Pida cotización después de que exista CGL. Independiente de SAM.gov. No invente una póliza.',
              docsLabel: 'SBA: Obtener seguro empresarial',
              sourceLabel: 'SBA: Obtener seguro empresarial',
              verifySteps: {
                'umbrella-need': {
                  text: 'Lea la cláusula de seguro. Si pide más que los límites primarios de CGL/auto, cotice paraguas. Si no, escriba que ningún contrato pide límites más altos.',
                  evidenceHint: 'Extracto de cláusula o nota de “sin límites más altos”',
                },
                'umbrella-bind-or-decline': {
                  text: 'Contrate cuando un aviso lo exija y el efectivo pueda quedarse sobre el piso operativo, o archive una declinación por escrito. Archive el COI cuando esté contratado. No marque completo sin esa prueba.',
                  evidenceHint: 'COI paraguas o declinación por escrito',
                },
              },
            },
          ),
          'insurance-crime': item(
            'Cobertura de crimen / fidelidad',
            'Crimen o fidelidad paga el hurto de empleados y a menudo la fidelidad de terceros cuando la empresa maneja dinero, tarjetas o valores del gobierno. El fraude por transferencia y las facturas falsas a menudo quedan fuera de una póliza ciber básica: pregunte si incluye ingeniería social y fraude en transferencias. Omita salvo que un aviso o un rol de tesorería cree el riesgo. No invente una póliza. No marque completo hasta que haya un COI, un endoso o un N/A por escrito.',
            'COI de crimen, endoso ciber o N/A por escrito',
            {
              completeLabel: 'Marcar completo (COI de crimen o N/A en archivo)',
              dependencyNote: 'Pida después de la cotización ciber para no comprar dos veces la misma cobertura de fraude. No invente una póliza.',
              docsLabel: 'SBA: Obtener seguro empresarial',
              sourceLabel: 'SBA: Obtener seguro empresarial',
              verifySteps: {
                'crime-need': {
                  text: 'Registre si la LLC guardará fondos de agencia, procesará pagos o tendrá acceso a cuentas de un comprador. Si la cotización ciber ya incluye ingeniería social y fraude en transferencias, anótelo en vez de comprar una segunda póliza.',
                  evidenceHint: 'Nota de rol o extracto de cotización ciber',
                },
                'crime-bind-or-na': {
                  text: 'Contrate crimen/fidelidad a nombre legal de la LLC, adjunte un endoso ciber o archive un N/A por escrito. Archive el COI cuando esté contratado. No marque completo sin esa prueba.',
                  evidenceHint: 'COI de crimen, endoso ciber o N/A por escrito',
                },
              },
            },
          ),
          'insurance-bonds': item(
            'Fianzas de cumplimiento y pago',
            'La Miller Act exige fianzas de cumplimiento y pago en la mayoría de la construcción federal de más de 150.000 USD. FAR 28.103-1 dice que las agencias en general no deben exigir esas fianzas en trabajo que no es construcción. La consultoría TI ordinaria registra N/A salvo que la solicitud lo pida. Las garantías de oferta siguen la misma regla. No compre un paquete de fianza que el aviso no exija. No invente un número de fianza.',
            'Fianza ejecutada o N/A por escrito (el valor por defecto de consultoría TI es N/A)',
            {
              completeLabel: 'Marcar completo (fianza o N/A en archivo)',
              docsLabel: 'FAR Subparte 28.1 — Fianzas',
              verifySteps: {
                'bonds-clause': {
                  text: 'Lea FAR 52.228-15 / 52.228-16 en el aviso. Cláusulas de construcción o desmantelamiento significan fianzas. Un aviso de servicios TI sin esas cláusulas es N/A.',
                  evidenceHint: 'Extracto de cláusula o nota N/A',
                },
                'bonds-proof': {
                  text: 'Archive la fianza ejecutada o un N/A por escrito. No marque completo sin esa prueba.',
                  evidenceHint: 'PDF de fianza o N/A por escrito',
                },
              },
            },
          ),
          'insurance-property': item(
            'Bienes del negocio / BOP',
            'Las pólizas de casa y de inquilino a menudo excluyen equipo del negocio. Si la LLC tiene laptops u otro equipo, pida bienes del negocio. Una BOP suele empaquetar eso con CGL: no compre un segundo CGL si el paquete ya lo incluye. No es un ítem FAR 28.307. No invente una póliza. No marque completo hasta que haya un COI, declaraciones BOP o un N/A por escrito.',
            'Declaraciones BOP o de bienes (asegurado nombrado = nombre legal de la LLC) o N/A por escrito',
            {
              completeLabel: 'Marcar completo (prueba de bienes o N/A en archivo)',
              dependencyNote: 'Coordine con CGL para que una BOP no duplique la responsabilidad general. No invente una póliza.',
              docsLabel: 'SBA: Obtener seguro empresarial',
              sourceLabel: 'SBA: Obtener seguro empresarial',
              verifySteps: {
                'property-need': {
                  text: 'Liste el equipo de la empresa (laptops, servidores, teléfonos). Si no hay, escriba N/A. Si el CGL irá dentro de una BOP, dígaselo al corredor para no comprar dos veces la responsabilidad general.',
                  evidenceHint: 'Lista de equipo o N/A',
                },
                'property-bind-or-na': {
                  text: 'Contrate bienes del negocio o una BOP a nombre legal de la LLC, o archive un N/A por escrito. Archive las declaraciones cuando esté contratado. No marque completo sin esa prueba.',
                  evidenceHint: 'Declaraciones BOP/bienes o N/A por escrito',
                },
              },
            },
          ),
          'insurance-epli': item(
            'Responsabilidad por prácticas de empleo (EPLI)',
            'EPLI paga reclamos como despido injusto, discriminación, acoso o represalias. No sustituye la compensación laboral. Pida cotización antes del primer empleado W-2. No compre hasta que exista esa contratación (o un contrato). No invente una póliza. No marque completo hasta que haya un COI o una nota escrita de “aún no hay empleados”.',
            'COI EPLI (asegurado nombrado = nombre legal de la LLC) o nota de espera por escrito',
            {
              completeLabel: 'Marcar completo (COI EPLI o nota de espera en archivo)',
              docsLabel: 'SBA: Obtener seguro empresarial',
              sourceLabel: 'SBA: Obtener seguro empresarial',
              verifySteps: {
                'epli-need': {
                  text: 'Si la LLC tiene empleados W-2, cotice EPLI. Solo miembros gestores y sin empleados es una nota de espera, no una brecha que deba comprar hoy.',
                  evidenceHint: 'Nota de plantilla',
                },
                'epli-bind-or-wait': {
                  text: 'Contrate a nombre legal de la LLC, o archive una nota de espera hasta el primer empleado. Archive el COI cuando esté contratado. No marque completo sin esa prueba.',
                  evidenceHint: 'COI EPLI o nota de espera por escrito',
                },
              },
            },
          ),
          'insurance-do': item(
            'Directores y oficiales (D&O)',
            'D&O paga si demandan a un dueño o gerente por cómo se dirige la empresa. No es CGL ni E&O. Una LLC de dos miembros puede esperar hasta que un contrato, un prestamista o un inversionista externo lo exija. No invente una póliza. No marque completo hasta que haya un COI o una nota de espera por escrito.',
            'COI D&O (asegurado nombrado = nombre legal de la LLC) o nota de espera por escrito',
            {
              completeLabel: 'Marcar completo (COI D&O o nota de espera en archivo)',
              docsLabel: 'SBA: Obtener seguro empresarial',
              sourceLabel: 'SBA: Obtener seguro empresarial',
              verifySteps: {
                'do-need': {
                  text: 'Cotice D&O si un aviso, un banco o un inversionista lo pide. Si no, escriba que está esperando.',
                  evidenceHint: 'Nota de pedido o de espera',
                },
                'do-bind-or-wait': {
                  text: 'Contrate a nombre legal de la LLC, o archive una nota de espera por escrito. Archive el COI cuando esté contratado. No marque completo sin esa prueba.',
                  evidenceHint: 'COI D&O o nota de espera por escrito',
                },
              },
            },
          ),
        },
      },
      cybersecurity: {
        label: 'Ciberseguridad',
        summary: 'Primero FCI (autoevaluación CMMC Nivel 1 en SPRS). El banco de niveles desbloquea NIST 800-171, SSP/POA&M y herramientas de enclave CUI solo cuando un contrato maneja CUI.',
        items: {
          'fci-cui-determination': item(
            'Determinación FCI frente a CUI',
            'Para cada oportunidad, lea el papel del trabajo y registre una etiqueta: FCI (información cotidiana del contrato → CMMC Nivel 1), CUI (información no clasificada más sensible → NIST 800-171 / CMMC Nivel 2) o Clasificado (Secret / Top Secret / habilitación de instalaciones). No asuma CMMC Nivel 2 ni una habilitación de instalaciones. Use Ayúdame a decidir si no está seguro.',
            'Extracto de cláusula de la solicitud + nota de determinación',
            {
              actionSteps: [
                'Abra la solicitud, la orden de trabajo o el pedido de personal de este trabajo.',
                'Busque en ese documento estas palabras exactas: CUI, Controlled Unclassified Information, DFARS 252.204-7012, NIST SP 800-171 y CMMC Level 2.',
                'Si encuentra alguna de esas palabras, escriba CUI. Eso incluye un trabajo de personal en el que la persona usará una computadora del gobierno que tiene CUI. CUI necesita NIST 800-171 / CMMC Nivel 2 después. Todavía no es clasificado.',
                'Si esas palabras no están, y el trabajo es ayuda de TI o personal no clasificado, escriba FCI. FCI es información cotidiana del contrato que no es pública. El primer nivel es CMMC Nivel 1. Solo con esa etiqueta no necesita CMMC Nivel 2 ni una habilitación de instalaciones.',
                'Escriba Clasificado solo cuando el aviso diga Secret, Top Secret o una habilitación de instalaciones. La habilitación personal de un dueño no es el FCL de la empresa. No adivine.',
                'Guarde la página donde encontró las palabras, o una nota de que buscó y no estaban.',
              ],
            },
          ),
          'data-impact-cloud-bar': item(
            'Nivel de impacto de datos (TX-RAMP / FedRAMP / SOC 2)',
            'Clasifique los datos que un producto en línea almacenaría antes de elegir una autorización de nube. Bajo: público o no confidencial; una filtración hace daño limitado — TX-RAMP Nivel 1 o FedRAMP Low. Nombres de inicio de sesión y páginas públicas caben aquí; los registros de agencia no. Normal: registros confidenciales del día a día; una filtración hace daño serio, no pérdida de vida — TX-RAMP Nivel 2 o FedRAMP Moderate (cerca del 80 por ciento de los productos de nube federal autorizados; el nivel habitual de agencias de Texas). Alto: orden público, seguridad nacional o una filtración catastrófica — FedRAMP High. Texas sigue usando TX-RAMP Nivel 2 y luego añade controles extra. TX-RAMP aplica solo a servicios de computación en la nube (Código de Gobierno de Texas 2054.0593), no a consultoría de mano de obra en los sistemas de una agencia. Las ciudades y los distritos escolares suelen pedir SOC 2 Tipo II. La reciprocidad de FedRAMP o GovRAMP no es automática después del 30 de oct. de 2024: pídala en SPECTRIM de DIR. No reclame una autorización que no tenga. Las empresas solo de consultoría registran que ninguna agencia guarda registros en su producto.',
            'Nota Bajo/Normal/Alto (o solo consultoría) + nivel exigido',
            {
              completeLabel: 'Marcar completo (clasificación en archivo)',
              docsLabel: 'Preguntas frecuentes TX-RAMP',
              sourceLabel: 'DIR de Texas — elegibilidad TX-RAMP',
              verifySteps: {
                'impact-classify': {
                  text: 'Para cada producto u oportunidad, escriba Bajo / Normal / Alto (o “solo consultoría — no hay registros de agencia alojados”). Los registros de agencia nunca son Bajo.',
                  evidenceHint: 'Nota de clasificación por producto u oportunidad',
                },
                'impact-bar': {
                  text: 'Mapee la clase a TX-RAMP Nivel 1 / FedRAMP Low, TX-RAMP Nivel 2 / FedRAMP Moderate, o FedRAMP High (Texas permanece en Nivel 2 más controles extra). Si el comprador es una ciudad o escuela, registre si pidieron SOC 2 en su lugar.',
                  evidenceHint: 'Nivel exigido, o N/A para consultoría solo de mano de obra',
                },
                'impact-reciprocity': {
                  text: 'Si usará FedRAMP o GovRAMP para cumplir Texas, envíe una solicitud TX-RAMP en SPECTRIM. No asuma que el producto está en la lista certificada de Texas. Empiece el paquete cuando haya un comprador listo; Texas puede dar estatus provisional de 18 meses durante una compra activa.',
                  evidenceHint: 'ID de solicitud SPECTRIM, o N/A',
                },
              },
            },
          ),
          'cmmc-l1-practices': item(
            'Prácticas CMMC Nivel 1 / FAR 52.204-21',
            'Implemente los 15 requisitos básicos de salvaguarda para FCI (control de acceso, identificación, medios, físico, comunicaciones, integridad). Algunos avisos más nuevos citan FAR 52.240-93 para los mismos 15 requisitos. El Nivel 1 es apto/no apto: no hay crédito POA&M.',
            'Carpeta de evidencia práctica por práctica',
          ),
          'cmmc-l1-scope': item(
            'Alcance de la evaluación CMMC Nivel 1',
            'Documente qué portátiles, inquilinos en la nube, correo y herramientas de colaboración procesan, almacenan o transmiten FCI. Aísle el FCI DoD de dispositivos personales y de cualquier acceso de personas no estadounidenses.',
            'Diagrama de alcance + lista de activos',
          ),
          'piee-sprs-access': item(
            'Cuenta PIEE y rol Cyber Vendor de SPRS',
            'Cuando se asigne el CAGE, regístrese en PIEE y solicite SPRS Cyber Vendor User para poder ingresar los resultados de Nivel 1.',
            'Usuario PIEE + confirmación del rol SPRS',
            { dependencyNote: 'Necesita un registro SAM Activo con CAGE. El banco no es necesario para la cuenta PIEE en sí.' },
          ),
          'sprs-attestation': item(
            'Alta y afirmación de Nivel 1 en SPRS',
            'Ingrese Final Level 1 (Self) en SPRS y haga que un oficial senior (socio administrador) afirme. Renueve cada año antes del vencimiento. No trate una puntuación numérica NIST 800-171 como sustituto del trabajo FCI de Nivel 1.',
            'ID de evaluación CMMC en SPRS + fecha de afirmación',
          ),
          'cmmc-uid-proposal': item(
            'Identificador único CMMC en las propuestas',
            'Cuando DFARS 252.204-7025 esté en la solicitud, ponga el identificador único CMMC (UID) de SPRS de cada sistema en alcance en la propuesta. Un UID existe solo después de publicar la evaluación. Los oficiales de contratación no adjudicarán sin un estatus CMMC vigente al nivel exigido y una afirmación anual vigente. El Nivel 1 no tiene ruta Condicional / POA&M: debe ser Final. CMMC Fase II (C3PAO) está en pausa; NIST SP 800-171 sigue aplicando bajo DFARS 252.204-7012 si hay CUI en alcance.',
            'Extracto de cláusula de la propuesta + UID(s) CMMC de SPRS',
            {
              dependencyNote: 'Necesita una evaluación SPRS de Nivel 1 (o superior) publicada para que exista un UID. Bloqueado en CAGE.',
            },
          ),
          'sprs-annual-renewal': item(
            'Recordatorio anual de reafirmación SPRS',
            'Calendario la afirmación anual de CMMC Nivel 1 antes del vencimiento en SPRS. Un socio administrador reafirma. Perder la ventana puede bloquear nuevas adjudicaciones aunque la evaluación original fuera Final.',
            'Recordatorio de calendario + última fecha de afirmación',
          ),
          'assessment-record-retention': item(
            'Retención de seis años del expediente de evaluación',
            'Conserve el paquete de autoevaluación de Nivel 1 (alcance, disposiciones de 15 prácticas, afirmación y artefactos) durante seis años. No almacene CUI en NextCompli comercial.',
            'Ubicación de retención + fecha de destrucción',
          ),
          'dfars-incident': item(
            'Preparación para reporte de incidentes DFARS',
            'Confirme contactos de reporte de incidentes cibernéticos en 72 horas, ruta de acceso DIBNet y fecha de simulacro para DFARS 252.204-7012 cuando CUI o información de defensa cubierta esté en alcance.',
            'Manual + última fecha de simulacro',
          ),
          'cmmc-gap': item(
            'Revisión de brechas NIST 800-171 / CMMC Nivel 2',
            'Si una solicitud objetivo exige protecciones CUI, mapee controles NIST SP 800-171, dueños e ítems POA&M abiertos. C3PAO de Fase II está en pausa; la autoevaluación aún puede exigirse bajo 7021: lea el aviso.',
            'Matriz de brechas o extracto de SSP',
          ),
          'ssp-poam-cui': item(
            'Plan de seguridad del sistema y POA&M (ruta CUI)',
            'Mantenga un SSP para el enclave que almacenaría CUI y un POA&M para requisitos 800-171 no cumplidos. No se exige para reclamar CMMC Nivel 1, pero sí antes de ofertar trabajo CUI.',
            'Copias fechadas de SSP + POA&M',
          ),
          'gcc-high-entra-intune': item(
            'Punteros de evidencia GCC High / Entra / Intune',
            'Cuando el CUI viva en un inquilino gubernamental de Microsoft, registre los ID de inquilino y exporte evidencia de controles Entra/Intune al expediente. No ingiera CUI en NextCompli comercial.',
            'ID de inquilino + ruta de exportación de evidencia (solo punteros)',
          ),
          'preveil-cui-enclave': item(
            'Buzón CUI cifrado (PreVeil o GCC High)',
            'Levante un buzón/Drive CUI cifrado cuando se vaya a enviar o compartir CUI. Las cuentas gratis de PreVeil son solo colaboración; CMMC Pass es de pago. El trabajo solo FCI no necesita este buzón.',
            'Dominio del enclave + lista de acceso (personas estadounidenses)',
          ),
        },
      },
      'shared-assurance': {
        label: 'SOC 2, CMMC y GSA',
        summary:
          'Un expediente de evidencia para el informe del auditor, el programa cibernético del DoD y una lista de GSA. Un informe SOC 2 no crea un estatus CMMC. Una lista es un contrato, no una certificación.',
        items: {
          'buyer-assurance-path': item(
            'Qué aseguramiento pidió realmente el comprador',
            'Para cada oportunidad, escriba el tipo de comprador y el nivel que nombró. El trabajo DoD usa CMMC y SPRS. Una suscripción de nube federal usa FedRAMP. Una ciudad, un condado, una escuela o un prime comercial suele pedir un informe SOC 2 Tipo II. Una lista de GSA es un contrato aparte. Registre N/A si nadie lo ha pedido. No responda un pedido de SOC 2 con una autoevaluación CMMC, ni una cláusula CMMC con un informe SOC 2.',
            'Comprador, nivel pedido y el sistema en alcance',
            {
              completeLabel: 'Marcar completo (nivel del comprador en archivo)',
              docsLabel: 'Cláusula FAR 52.204-21',
              sourceLabel: 'Programa CMMC del DoD',
            },
          ),
          'soc2-scope': item(
            'Sistema SOC 2 y Criterios de Servicios de Confianza',
            'Nombre el sistema que cubriría un informe SOC 2, el período y si el comprador quiere Tipo I (diseño en una fecha) o Tipo II (efectividad operativa durante meses). Seguridad es el criterio base. Agregue Disponibilidad, Confidencialidad, Integridad del procesamiento o Privacidad solo cuando un contrato lo pida. Una carta de preparación de un consultor no es el informe del CPA. Archive N/A si ningún comprador ha pedido SOC 2.',
            'Límite del sistema, criterios en alcance, Tipo I o Tipo II, o un N/A fechado',
            {
              completeLabel: 'Marcar completo (alcance o N/A en archivo)',
              sourceLabel: 'SOC 2 del AICPA',
            },
          ),
          'shared-evidence-library': item(
            'Una biblioteca de evidencia para SOC 2 y CMMC',
            'Guarde una sola vez los artefactos de acceso, registros, cambios, respuesta a incidentes y riesgo. Etiquete cada archivo con el Criterio de Servicios de Confianza que apoya y, cuando haya FCI o CUI en alcance, con la práctica CMMC correspondiente. Cruce por NIST SP 800-53: use el mapa del AICPA y luego el Apéndice D de NIST SP 800-171. No guarde una segunda carpeta con las mismas capturas. La pausa de la Fase II no retira el trabajo de NIST SP 800-171 que un contrato con CUI ya exige.',
            'Índice de artefactos con el criterio y la práctica CMMC que cada uno apoya',
            {
              completeLabel: 'Marcar completo (índice en archivo)',
              docsLabel: 'Mapa del AICPA a NIST 800-53',
              sourceLabel: 'NIST SP 800-171 Rev. 2',
            },
          ),
          'soc2-cmmc-gaps': item(
            'Brechas que no viajan entre SOC 2 y CMMC',
            'Escriba las brechas del comprador actual. Cuando hay CUI en alcance, un informe SOC 2 aún deja el cifrado validado por FIPS, las marcas de medios CUI, un plan de seguridad del sistema, el bloqueo de sesión y el acompañamiento de visitantes para cerrar bajo NIST SP 800-171. CMMC no cubre Disponibilidad, Integridad del procesamiento ni Privacidad de SOC 2. El trabajo solo FCI puede marcar las brechas de CUI como N/A. No trate una opinión limpia de SOC 2 como un estatus CMMC bajo 32 CFR Parte 170.',
            'Lista de brechas con dueño, o un N/A fechado de solo FCI',
            {
              completeLabel: 'Marcar completo (brechas o N/A en archivo)',
              sourceLabel: '32 CFR Parte 170',
            },
          ),
          'soc2-report-custody': item(
            'Custodia del informe SOC 2',
            'Cuando un despacho de CPA termine el examen, registre el despacho, la opinión, el período cubierto y dónde está el PDF. Anote cada organización de subservicio (nube, correo, proveedor de identidad) y si su informe está incluido o excluido. No cargue aquí el informe completo si describe el sistema en detalle. Una carta puente cubre solo los meses posteriores al período, y solo cuando el despacho la escribe.',
            'Despacho, período, opinión, ruta de almacenamiento y nota de subservicio, o N/A',
            {
              completeLabel: 'Marcar completo (nota de custodia en archivo)',
              dependencyNote: 'Necesita un alcance de sistema nombrado, o una nota fechada de que ningún comprador pidió SOC 2.',
            },
          ),
          'gsa-mas-lifecycle': item(
            'Administración de la lista de GSA después de la adjudicación',
            'Después de que GSA adjudique una Lista de Adjudicación Múltiple, nombre un administrador del contrato y mantenga SAM Active hasta el pago final (FAR 52.204-7). El piso de ventas de GSAR 552.238-79 es $100,000 en los primeros cinco años y $125,000 en cada período de cinco años después. Reporte servicios antes del 31 de octubre bajo FAR 52.204-15. Actualice la información de responsabilidad en SAM al ritmo de FAR 52.209-9. Vuelva a representar el tamaño antes de una opción bajo FAR 52.219-28. Acepte modificaciones masivas en eMod. Reporte las ventas como las nombre el contrato. No invente un número de contrato. Si GSA no ha adjudicado una lista, archive esa espera en la tarea de la lista y marque esto N/A.',
            'Número de contrato que asignó GSA, administrador, próxima fecha de reporte, o N/A',
            {
              completeLabel: 'Marcar completo (calendario de administración o N/A en archivo)',
              dependencyNote: 'Necesita un registro SAM Active. Empieza solo después de que GSA adjudique la lista.',
              docsLabel: 'Guía de modificaciones MAS',
              sourceLabel: 'Requisitos después de un contrato MAS',
            },
          ),
          'gsa-taa-supply': item(
            'Ley de Acuerdos Comerciales en una lista de GSA',
            'Para cada producto ofrecido en una lista, conserve el país de origen y muestre que es un producto final de EE. UU. o de un país designado bajo FAR 52.225-5. Los servicios siguen la misma regla de la Ley de Acuerdos Comerciales. Actualice la lista cuando un proveedor cambie la planta. Las representaciones de la Sección 889 permanecen en SAM. Las ofertas solo de mano de obra, sin productos, pueden archivar N/A para el país de origen y aun así mantener vigente la representación 889.',
            'Lista de país de origen, o un N/A de solo mano de obra',
            {
              completeLabel: 'Marcar completo (lista de origen o N/A en archivo)',
              dependencyNote: 'Aplica cuando una oferta de lista incluye productos. La Sección 889 sigue en la tarea de SAM.',
              docsLabel: 'Cláusula FAR 52.225-5',
              sourceLabel: 'TAA en MAS',
            },
          ),
        },
      },
      'contract-compliance': {
        label: 'Cumplimiento contractual',
        summary: 'Obligaciones FAR/DFARS, 889, FOCI / acceso de personas extranjeras, flujo hacia subcontratistas y controles de exportación.',
        items: {
          'clause-register': item(
            'Registro de cláusulas FAR/DFARS',
            'Mantenga un registro accionable de cláusulas de cada solicitud y adjudicación (ciber, 889, reportes, IP, OCI) con dueño y fechas.',
            'Enlace al registro de cláusulas',
          ),
          'section-889': item(
            'Telecomunicaciones cubiertas de la Sección 889',
            'Prohíba Huawei, ZTE, Hytera, Hikvision, Dahua y equipo cubierto sucesor en el entorno de la LLC y en cualquier entrega DoD. Complete las representaciones SAM/FAR 52.204-24 / 52.204-25.',
            'Representación 889 + atestación de activos',
          ),
          'foci-foreign-persons': item(
            'Acceso FCI/CUI solo para personas estadounidenses (FOCI)',
            'Adopte una regla escrita: solo personas estadounidenses pueden acceder a FCI/CUI DoD. Los contratistas extranjeros, incluido cualquier plan de personal remoto en Venezuela, quedan fuera de sistemas, buzones y repositorios DoD. Declare con honestidad la propiedad, control o influencia extranjera en SAM.',
            'Política de acceso adoptada + organigrama',
          ),
          'flow-down': item(
            'Verificación de flujo a subcontratistas',
            'Verifique que las cláusulas de ciber, 889 y ética se fluyan antes de que un sub toque FCI. No use mano de obra de personas no estadounidenses en trabajo DoD en alcance.',
            'Ejemplo de anexo de subcontrato',
          ),
          'export-gov-property': item(
            'Controles de exportación y puntos de propiedad gubernamental',
            'Confirme el cribado ITAR/EAR para datos técnicos y el manejo de propiedad gubernamental. La consultoría TI no clasificada aún necesita un valor predeterminado de “no exportar datos técnicos”.',
            'Enlaces a SOP + última revisión',
          ),
          'facility-clearance': item(
            'Habilitación de instalaciones (solo si es clasificado)',
            'Escriba el estado de la habilitación de instalaciones (FCL) de la empresa en lenguaje claro: ninguna, en trámite, o habilitada y a qué nivel. La habilitación personal de un dueño no es el FCL de la empresa. Un Top Secret personal puede estar atado a un solo contrato a la vez. Algunos avisos solo piden una investigación de personal más baja. Un DD Form 254 indica el nivel de seguridad de ese contrato. No gestione un FCL salvo que una solicitud clasificada lo exija. La consultoría TI DoD no clasificada archiva un N/A por escrito.',
            'Determinación N/A o paquete FCL',
          ),
        },
      },
      'supply-chain-assurance': {
        label: 'Aseguramiento de la cadena de suministro',
        summary: 'Riesgo de proveedores, fuentes prohibidas y trazabilidad para primes, herramientas y subcontratistas.',
        items: {
          'vendor-compliance': item(
            'Seguimiento de cumplimiento de subcontratistas',
            'Clasifique proveedores clave (nube, MSP, subcontratistas) según si tocarán FCI/CUI y confirme que las atestaciones requeridas estén en archivo.',
            'Lista de riesgo de proveedores',
          ),
          'prohibited-source': item(
            'Cribado de fuentes prohibidas',
            'Ejecute chequeos OFAC / exclusiones SAM / 889 para proveedores en alcance y dueños beneficiarios antes de incorporarlos.',
            'Fecha del registro de cribado',
          ),
          traceability: item(
            'Registros de trazabilidad',
            'Confirme trazabilidad de proveedor, licencia y entregables para software y artefactos de consultoría en contratos activos.',
            'Muestra de lote/traza o procedencia del repositorio',
          ),
        },
      },
      'financial-ethics': {
        label: 'Controles financieros y de ética',
        summary: 'Prefiera FFP/T&M de ítems comerciales para primeros premios; tenga lista la contabilidad de tiempo y la divulgación.',
        items: {
          'dcaa-accounting': item(
            'Preparación contable alineada con DCAA',
            'Para primeros premios TI DoD, prefiera precio fijo firme o T&M comercial para que un sistema de costos pleno DCAA no sea una puerta. Documente la decisión. No oferte reembolso de costos hasta que los libros lo soporten.',
            'Notas del sistema contable + política de tipo de contrato',
          ),
          'timekeeping-billing': item(
            'Controles de tiempo y facturación',
            'Registro diario de tiempo por contrato/CLIN, sin redondeos tramposos, revisión de factura antes de enviarla. Se exige incluso en T&M.',
            'Firma de la lista de controles',
          ),
          'ethics-disclosure': item(
            'Programa de ética y divulgación obligatoria',
            'Confirme la recepción de conflictos de interés y la ruta de divulgación obligatoria FAR 52.203-13 (fraude, soborno, gratificaciones) incluso como LLC pequeña.',
            'SOP de divulgación + registro de capacitación',
          ),
        },
      },
      'pursuit-readiness': {
        label: 'Preparación para captar contratos',
        summary: 'Declaración de capacidades, Búsqueda de Pequeñas Empresas de la SBA, SAM.gov y GSA, adjudicaciones de investigación SBIR y STTR, ESBD y CMBL de Texas, RFO de DIR, cooperativas, portales de ciudad y condado (ejemplos San Antonio / Bexar), tableros de la ciudad sede, portales de distritos escolares, NIGP frente a NAICS, ayuda APEX y una primera adjudicación vía subcontratación.',
        items: {
          'capability-statement': item(
            'Declaración de capacidades',
            'Publique la misma declaración como página de texto y como PDF de una página. La página es lo que leen los compradores y los buscadores. El PDF es el documento para dejar. Ponga el nombre legal, el UEI, el CAGE, el NAICS, el PSC, el teléfono y el correo donde se vean primero. Luego liste solo el trabajo que la empresa puede hacer, en lenguaje claro: sitios web, sitios accesibles (WCAG y Sección 508), aplicaciones web y en la nube, integraciones, datos y tableros, automatización, y gobernanza de IA cuando ese trabajo sea real. Separe el desempeño previo en dos listas con nombre: trabajo que hizo esta empresa, y trabajo que hizo un dueño antes de que existiera la empresa. El trabajo comercial puede ir en cualquiera de las dos cuando no hay adjudicación de gobierno, y la página debe decir cuál lista es. Agregue cualquier reserva que realmente tenga. Los códigos deben coincidir con SAM. No copie el UEI, el CAGE, el número de lista GSA MAS ni el número OASIS+ de otra empresa. Mantenga el PDF bajo 10 MB y etiquetado para que un lector de pantalla lo lea. Cuando DFARS 252.204-7025 esté en un aviso, incluya el UID CMMC de SPRS. Las oficinas OSBP del Ejército también aceptan el archivo por el eKiosk de APG.',
            'PDF + fecha de última revisión',
            { dependencyNote: 'La declaración debe mostrar los mismos códigos PSC que eligió para la página pública.' },
          ),
          'dsbs-profile': item(
            'Búsqueda de Pequeñas Empresas de la SBA (SBS, antes DSBS)',
            'Cuando SAM esté Active, complete el perfil de Búsqueda de Pequeñas Empresas de la SBA (SBS, antes Dynamic Small Business Search) para que los primes puedan encontrar la LLC para reservas y subcontratación. No invente una URL de perfil.',
            'URL del perfil SBS',
            {
              sourceLabel: 'Búsqueda de Pequeñas Empresas de la SBA',
            },
          ),
          'sam-opportunity-watch': item(
            'Vigilancia de oportunidades en SAM.gov',
            'NextCompli ya consulta la API pública oficial de oportunidades para NAICS 541512. Guarde también esa búsqueda en sam.gov para recibir el correo de SAM. Revise cada semana; no oferte avisos vencidos o solo CUI hasta que la Puerta 2 esté lista. No rastree sam.gov.',
            'Lista de oportunidades en la consola + nombres de búsquedas guardadas',
          ),
          'apex-accelerator': item(
            'Asesoría APEX Accelerator',
            'Reserve una cita con Texas APEX Accelerator para revisar el registro SAM, criticar la declaración de capacidades y la estrategia del primer bid. Recurso gubernamental sin costo.',
            'Fecha de asesoría + notas',
            {
              docsLabel: 'Encontrar un acelerador APEX',
              sourceLabel: 'Aceleradores APEX de la SBA',
            },
          ),
          'saeps-vendor': item(
            'Proveedor SAePS de la Ciudad de San Antonio',
            'Registre una sola vez en el portal de e-adquisiciones de cada ciudad en la que vaya a ofertar. San Antonio usa SAePS para licitaciones de bienes y servicios profesionales de la Ciudad, incluida TI. Una cuenta por proveedor y un correo de notificación. Soporte a proveedores: vendors@sanantonio.gov, 210-207-0118. Construcción, arquitectura e ingeniería usan CIVCAST: TI y servicios profesionales permanecen en SAePS. Si esta empresa no ofertará trabajo de San Antonio, archive un N/A por escrito. Esto no sustituye SAM.gov y no es motivo para iniciar un segundo registro SAM.',
            'Confirmación SAePS + User ID (no la contraseña)',
            {
              completeLabel: 'Marcar completo (perfil SAePS guardado)',
              dependencyNote: 'Necesita la LLC y el EIN. Independiente de SAM.gov. Una cuenta SAePS por proveedor. No use CIVCAST para TI.',
              docsLabel: 'Registrarse como proveedor COSA (PDF)',
              sourceLabel: 'Convertirse en proveedor (SAePS)',
              verifySteps: {
                'saeps-preregister': {
                  text: 'Abra Become a Vendor, use Chrome y envíe New Vendor Registration una sola vez. Use el nombre legal de la LLC, el EIN como Employer Identification Number (no SSN) y un correo de notificación. Si Dun & Bradstreet ya emitió un DUNS, escríbalo; no ponga el EIN en D-U-N-S. El fax es obligatorio: use el número de voz de la LLC. La dirección física es la dirección de operación de la empresa, no un buzón de agente registrado. No cree una segunda cuenta SAePS.',
                  evidenceHint: 'Captura de Vendor Submission Confirmation',
                },
                'saeps-credentials': {
                  text: 'Espere hasta 48 horas hábiles a que Vendor Support envíe por correo el User ID y la contraseña temporal de SAePS después de la validación TIN del IRS. Guarde el User ID y la contraseña en su administrador de contraseñas bajo el nombre legal de la LLC / SAePS. No ponga la contraseña en NextCompli, Confluence, Jira ni git. Llame al 210-207-0118 o escriba a vendors@sanantonio.gov si no llega.',
                  evidenceHint: 'Correo de credenciales SAePS (la contraseña vive en el administrador de contraseñas, no aquí)',
                },
                'saeps-profile-nigp': {
                  text: 'Inicie sesión, cambie la contraseña, fije la pregunta de seguridad, termine la pestaña Vendor Information de una sentada y agregue códigos NIGP que coincidan con el trabajo (use la tabla cruzada NAICS). No se registre en CIVCAST para trabajo de TI o servicios profesionales.',
                  evidenceHint: 'Confirmación de perfil SAePS + NIGP guardado',
                },
              },
            },
          ),
          'nigp-code-alignment': item(
            'Códigos NIGP frente a NAICS',
            'Elija códigos de clase/ítem NIGP en cada perfil de ciudad, condado y CMBL para que los avisos coincidan con consultoría TI. SAM.gov usa NAICS, no NIGP. No elija códigos de construcción u obras públicas que la empresa no pueda cumplir. Termine el NIGP de SAePS cuando llegue el User ID de la Ciudad.',
            'Lista NIGP guardada en SAePS, CMBL y Bexar (o una nota de espera fechada)',
            {
              completeLabel: 'Marcar completo (códigos NIGP guardados)',
              dependencyNote: 'Necesita la LLC y el EIN. Independiente de SAM.gov. No invente códigos.',
              sourceLabel: 'Códigos NIGP (Ciudad de San Antonio)',
            },
          ),
          'gsa-schedule': item(
            'Lista de Adjudicación Múltiple de GSA',
            'Una Lista de Adjudicación Múltiple de GSA es un contrato que se gana cuando SAM.gov está Active. No es un alta, no es el registro SAM y no es una certificación SOC 2 ni CMMC. OASIS+ es otro vehículo de servicios profesionales para todo el gobierno. También se gana cuando SAM está Active, y no es un alta. Lea la licitación vigente y responda solo cuando el trabajo coincida. Después de una adjudicación de lista, mantenga el calendario de administración y la lista de la Ley de Acuerdos Comerciales en la sección SOC 2, CMMC y GSA. No diga que la empresa tiene una lista o OASIS+ hasta que esa agencia la adjudique. No copie el número de un prime en su declaración.',
            'Licitación que responderá, adjudicación GSA, o una nota de que espera a que SAM esté Active',
            {
              completeLabel: 'Marcar completo (adjudicación en archivo o espera documentada)',
              dependencyNote: 'Necesita un registro SAM Active. No invente un número de lista.',
              sourceLabel: 'Vender al gobierno (GSA)',
            },
          ),
          'esbd-watch': item(
            'Avisos ESBD de Texas',
            'Busque en el Boletín Electrónico de Negocios del Estado las licitaciones de consultoría TI y servicios profesionales. Puede buscar y ofertar sin pagar el CMBL. Vigilar el ESBD no es un contrato DIR, no es un certificado HUB y no es un registro SAM.',
            'La búsqueda del ESBD que está vigilando, o una nota fechada de que ninguna coincide',
            {
              completeLabel: 'Marcar completo (búsqueda vigilada en archivo)',
              dependencyNote: 'Independiente de SAM.gov. No invente estatus DIR.',
              sourceLabel: 'ESBD de Texas',
            },
          ),
          'cmbl-registration': item(
            'Lista CMBL de postores de Texas',
            'Regístrese en la Lista Maestra Centralizada de Postores del Contralor después del EIN si quiere avisos por correo para los códigos de clase que elija. La tarifa anual publicada es 70 USD. Se exige EIN; el Contralor no acepta SSN. El CMBL no es obligatorio para ofertar en el ESBD y no es un contrato DIR. Omita los 70 USD hasta que el efectivo pueda quedarse sobre el piso operativo. Consulte VPTS (sistema de seguimiento de desempeño de proveedores) después del primer premio estatal.',
            'Recibo del CMBL, o una nota fechada de que omite los 70 USD por ahora',
            {
              completeLabel: 'Marcar completo (CMBL o nota de omisión en archivo)',
              dependencyNote: 'Necesita el EIN. Independiente de SAM.gov. No invente un número de proveedor CMBL.',
              docsLabel: 'Directorio CMBL / HUB',
              sourceLabel: 'Registro de proveedores del Contralor',
            },
          ),
          'tx-dir-rfo': item(
            'Contratos TI de DIR de Texas',
            'Los contratos cooperativos de TI de DIR se ganan respondiendo un Request for Offer en ESBD / Tech4TX. El correo del CMBL ayuda a ver el RFO pero no es obligatorio. No puede inscribirse en DIR como se registra en SAM. No invente estatus de proveedor DIR. Responda solo cuando el trabajo coincida.',
            'RFO que responderá, adjudicación DIR, o una nota de que vigila el ESBD primero',
            {
              completeLabel: 'Marcar completo (adjudicación en archivo o espera documentada)',
              dependencyNote: 'Necesita la LLC y el EIN. No invente un número de contrato DIR.',
              sourceLabel: 'Cómo convertirse en proveedor DIR',
            },
          ),
          'cooperative-purchasing': item(
            'BuyBoard, TIPS y HGACBuy',
            'BuyBoard, TIPS (ESC Región 8) y HGACBuy son catálogos cooperativos opcionales de los que compran ciudades, condados y escuelas. Se gana un asiento respondiendo su licitación. El registro no es una adjudicación. No invente un número de contrato cooperativo. Omita hasta que el efectivo y la capacidad permitan una respuesta.',
            'Licitación cooperativa que responderá, adjudicación, o una nota de omisión fechada',
            {
              completeLabel: 'Marcar completo (adjudicación o nota de omisión en archivo)',
              dependencyNote: 'Necesita la LLC y el EIN. No invente un número de contrato BuyBoard, TIPS o HGACBuy.',
              docsLabel: 'Cooperativa BuyBoard',
              sourceLabel: 'Cooperativa TIPS',
            },
          ),
          'school-isd-portals': item(
            'Portales de proveedores de distritos escolares',
            'Cada distrito escolar independiente de Texas lleva un portal de proveedores (Bonfire Interactive, IonWave, PlanetBids u otro). Regístrese solo en los distritos que vaya a servir. El registro no es una adjudicación. Guarde cada acceso en su administrador de contraseñas. No ponga contraseñas aquí. Archive un N/A por escrito si ningún ISD está en alcance.',
            'User IDs de portales de distrito, o una nota fechada de que ningún ISD está en alcance aún',
            {
              completeLabel: 'Marcar completo (portales o nota de omisión en archivo)',
              sourceLabel: 'Distritos escolares de Texas',
            },
          ),
          'neighbor-county-portals': item(
            'Portales de licitación de condados vecinos',
            'Los condados junto al condado sede pueden usar BidNet Direct, CivicPlus u otro tablero en vez del portal propio de ese condado. Ejemplo: el Condado de Hays usa BidNet Direct junto al Portal de Proveedores de Bexar. Regístrese una vez por portal para los condados que vaya a servir. Guarde los accesos en su administrador de contraseñas. No ponga contraseñas aquí. Archive un N/A por escrito si solo un condado está en alcance.',
            'User IDs de portales, o una nota fechada de que solo Bexar está en alcance',
            {
              completeLabel: 'Marcar completo (portales o nota de omisión en archivo)',
              sourceLabel: 'BidNet Direct para condados vecinos',
            },
          ),
          'bexar-bids': item(
            'Portal de proveedores del Condado de Bexar',
            'Regístrese en el portal de proveedores de cada condado que vaya a servir. El Condado de Bexar es el ejemplo del sur de Texas: adjunte solo códigos de mercancía que la empresa pueda cumplir y el condado envía avisos de licitación para esos códigos. Tenga un W-9 listo antes de que el condado le pague. Guarde el acceso del portal en su administrador de contraseñas. No ponga la contraseña aquí. Archive un N/A por escrito si esta empresa no servirá ese condado.',
            'Registro de proveedor completo (solo User ID)',
            {
              completeLabel: 'Marcar completo (registro de Bexar guardado)',
              sourceLabel: 'Registro de proveedores del Condado de Bexar',
            },
          ),
          'helotes-bids': item(
            'Avisos de licitación de la ciudad sede',
            'Revise la página de avisos de licitación de Finanzas de la ciudad sede. Algunas ciudades (Helotes es el ejemplo) no envían los avisos por correo y no usan un portal de e-adquisiciones. Responda solo cuando un aviso coincida con el trabajo. No se exige una cuenta de proveedor aparte para leer un tablero de solo vigilancia. Archive un N/A por escrito si esta empresa no servirá esa ciudad.',
            'Aviso que está vigilando, o una nota fechada de que ninguno coincide',
            {
              completeLabel: 'Marcar completo (nota de vigilancia en archivo)',
              sourceLabel: 'Avisos de licitación de la ciudad sede',
            },
          ),
          'sbir-sttr-fit': item(
            'Encaje SBIR o STTR',
            'Escriba si esta captura es SBIR, STTR o ninguna. El oferente debe ser una pequeña empresa con fines de lucro de EE. UU. con menos de 500 empleados. SBIR no exige un socio de investigación. STTR exige una institución de investigación sin fines de lucro: la empresa hace al menos el 40 por ciento del trabajo y la institución al menos el 30 por ciento. Quien solicita es siempre la pequeña empresa. Una universidad no puede solicitar. No marque esto como terminado para una empresa que no es pequeña.',
            'Nota escrita del encaje (SBIR, STTR o no encaja) y el número de empleados',
          ),
          'sbir-phase-i': item(
            'Propuesta de Fase I de SBIR o STTR',
            'La Fase I prueba que la idea puede funcionar y tiene un camino comercial. Suele durar de 6 a 12 meses. Desde abril de 2026 el tope de la SBA sin exención es 323,090 USD, incluidas las modificaciones. Lea el tema de defensa: puede fijar un tope menor. Presente las propuestas de defensa en DSIP. Estas adjudicaciones son contratos, no subvenciones. La empresa conserva la invención. NextCompli no presenta la propuesta. No invente un número de adjudicación. Si ningún tema encaja, archive una nota con fecha y deje la adjudicación en blanco.',
            'Número de tema y recibo de DSIP, o una nota fechada de que no hay tema abierto',
            { dependencyNote: 'Escriba el encaje SBIR o STTR antes de gastar una propuesta en un tema.' },
          ),
          'sbir-phase-ii': item(
            'Prototipo de Fase II de SBIR o STTR',
            'La Fase II continúa el resultado de la Fase I y construye un prototipo, por lo general hasta dos años. Desde abril de 2026 el tope de la SBA sin exención es 2,153,927 USD, incluidas las modificaciones. Un tema puede fijar un tope menor. Solo un ganador de Fase I es elegible, salvo que ese tema permita ir directo a Fase II porque el trabajo de viabilidad ya se hizo con otro dinero. Los temas de defensa suelen permitir una Fase II de seguimiento por empresa y por tema. No invente un número de adjudicación.',
            'Adjudicación o número de tema de Fase II, o una nota de que la Fase I no está terminada',
            { dependencyNote: 'Termine el encaje y la ruta de propuesta de Fase I antes de registrar una Fase II.' },
          ),
          'sbir-phase-iii': item(
            'Seguimiento de Fase III de SBIR o STTR',
            'La Fase III vende o pone en uso el resultado. No se paga con dinero reservado de SBIR o STTR. No hay tope de dólares, tiempo ni cantidad de adjudicaciones. La agencia debe adjudicar el seguimiento a la empresa que desarrolló la tecnología, en la mayor medida posible, incluido un contrato de fuente única, cuando el trabajo deriva de, extiende o completa el esfuerzo anterior. Conserve la cláusula de derechos de datos DFARS 252.227-7018. El período de protección actual corre 20 años desde la fecha de adjudicación, salvo que las partes acuerden después otro período. No invente un número de contrato.',
            'Número de contrato de seguimiento del comprador, o una nota de que no hay Fase III abierta',
            { dependencyNote: 'Una Fase III tiene que derivar de una adjudicación real de Fase I o Fase II. No abra una desde un tema en blanco.' },
          ),
          'first-subcontract-path': item(
            'Primera adjudicación vía subcontratación',
            'Construya las primeras adjudicaciones como subcontratista o miembro de equipo, empezando por trabajo de ciudad y condado, antes de ofertar como prime. Muchos avisos esperan unos dos años de historia de la empresa antes de que pueda ser prime. Lea cada aviso por una brecha que esta empresa no tiene: CMMC Nivel 2, una autoridad para operar (ATO), una habilitación de instalaciones (FCL), un DD Form 254, NAICS 54151 HACS, una certificación o un límite de seguro ciber. Un acuerdo de equipo nombra quién cubre cada brecha. No ponga el número GSA MAS u OASIS+ del prime en su declaración de capacidades. Lea el flujo CMMC del prime antes de firmar.',
            'Lista de primes objetivo + registro de contactos',
          ),
        },
      },
    },
  },
  'website-compliance': {
    title: 'Cumplimiento del sitio web',
    subtitle:
      'Nombre primero la cuenta y el sitio. El escaneo de portada registra si la página responde (incluido un 504), HTTPS, encabezados de seguridad, la etiqueta de ancho de teléfono, un título y una descripción, enlaces de privacidad/cookies/DMCA y autenticación de correo (SPF/DKIM/DMARC). Luego trabaje la lista: la página responde, cabe en un teléfono, búsqueda, datos de salud, datos personales, sin archivos de gobierno en el sitio público, privacidad, accesibilidad, seguridad, correo, Zero Trust, barreras de entrada de IA/ML cuando el sitio tiene un chat o asistente, un linter antes del deploy, PI y reglas al consumidor. Los planes superiores añaden monitoreo y gobernanza. El escaneo no lee código privado y no certifica HIPAA.',
    notices: {
      'not-legal-advice': {
        title: 'Evidencia de lista, no asesoría legal',
        body: 'Use esta consola para registrar dueños, URL y fechas de revisión. No certifica cumplimiento ni sustituye a un abogado. Mapee cada ítem a las leyes que realmente aplican a su audiencia y flujos de datos.',
      },
      'website-law-baseline': {
        title: 'Cubra DMCA y las demás reglas del sitio',
        body: 'Las obligaciones básicas de sitios EE.UU./UE incluyen DMCA 17 U.S.C. § 512, GDPR y CCPA/CPRA (más otras leyes estatales de privacidad), COPPA si hay menores de 13, ADA/WCAG, publicidad FTC y reglas ROSCA/opción negativa, clickwrap E-SIGN, CAN-SPAM, autenticación de remitente de Gmail/Yahoo/Microsoft (SPF/DKIM/DMARC), PCI DSS si acepta tarjetas, higiene Zero Trust de CIS / CISA para la nube, contenedores, repos git y el sitio público, y — cuando el sitio expone un chat u otra IA generativa — barreras de entrada y salida para que intentos tóxicos, fuera de tema o de inyección no lleguen al modelo sin filtrar (Top 10 de OWASP para aplicaciones LLM). Las reglas locales, estatales y sectoriales aún necesitan una matriz de aplicabilidad.',
      },
      'plan-coverage-ladder': {
        title: 'La cobertura se profundiza por suscripción',
        body: 'CompliCore es higiene CIS Implementation Group 1 / CISA tradicional-a-inicial: SPF que lista el servidor de correo, una clave DKIM, DMARC en p=none con buzón de informes, una nota de acción cuando el correo exige una decisión, HTTPS, encabezados, MFA, git privado, sin secretos en imágenes, e inventario fechado de cualquier chat o asistente de IA público (o nota de que no hay). CompliAdvantage añade CIS IG2 / CISA avanzado: cuarentena DMARC, baja de un clic, tasa de quejas, bitácoras CIS Benchmark, ramas protegidas, escaneo de imágenes/SBOM, monitoreo en vivo y rieles de entrada de IA que bloquean prompts tóxicos, políticos/fuera de tema e inyección antes del modelo. CompliComplete es CIS IG3 / CISA óptimo: DMARC reject, BIMI, CSPM, imágenes firmadas, SSO y auditoría de git, revisiones de evidencia con consultores embebidos y gobernanza de IA con evaluación de la capa de barreras.',
      },
      'cmp-competitive-baseline': {
        title: 'Capacidades CMP que esta consola ahora rastrea',
        body: 'Cookiebot, CookieYes, Termly, iubenda, Osano y CMP similares venden inventario de cookies, bloqueo de scripts hasta el consentimiento, registros de consentimiento, Google Consent Mode v2, banners geográficos y reescaneos programados. El escaneo de portada de NextCompli detecta proveedores CMP, hosts de rastreo por propósito (esencial / publicidad / analítica / rendimiento / social), enlaces GPC/No vender y Limitar SPI, y widgets overlay. Un overlay de accesibilidad (UserWay/accessiBe) no sustituye WCAG.',
      },
    },
    pillars: {
      'public-site-health': {
        label: 'El sitio está arriba y es seguro de usar',
        summary: 'La página responde, cabe en un teléfono, se puede encontrar en la búsqueda y no guarda datos de salud, datos personales ni archivos de gobierno que no deba. El escaneo de portada registra el código de estado, la etiqueta de ancho de teléfono y el título y la descripción. Una persona sigue mirando la página.',
        items: {
          'website-compliance-calendar': item(
            'Ponga las fechas de este trabajo en el calendario',
            'Abra el calendario de esta página y guarde una fecha para el próximo escaneo, la revisión de políticas y la renovación del dominio. Elija No este año solo cuando esa fila no aplique. No invente una fecha.',
            'Cada fecha que aplica, guardada en el calendario',
            {
              completeLabel: 'Marcar completo (fechas en el calendario)',
              verifySteps: {
                'site-rescan': {
                  text: 'Guarde la fecha del próximo escaneo de la homepage, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
                'policy-review': {
                  text: 'Guarde la fecha de la revisión de privacidad, cookies y términos, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
                'domain-renewal': {
                  text: 'Guarde la fecha de renovación del dominio, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
              },
            },
          ),
          'site-answers-not-504': item(
            'La página responde (no es un 504)',
            'Abra el sitio. Carga una página que funciona. No ve 504 Gateway Timeout ni 502 Bad Gateway. El escaneo de portada registra el código de estado. Un código de 200 a 399 significa que la página respondió.',
            'Código de estado del escaneo, o una captura de la página que funciona',
            {
              actionSteps: [
                'Abra la dirección del sitio en un navegador.',
                'Carga una página que funciona. No ve “504 Gateway Timeout” ni “502 Bad Gateway”.',
                'Si ve 504, el servidor no contestó a tiempo. Avise a quien hospeda el sitio.',
                'Ejecute el escaneo de portada. Escribe el código de estado en esta fila. 200 a 399 significa que la página respondió.',
              ],
            },
          ),
          'site-fits-a-phone': item(
            'La página cabe en un teléfono',
            'Abra el sitio al ancho de un teléfono y en una pantalla ancha. Las palabras se leen sin deslizar de lado y los botones se pueden tocar. El escaneo marca si falta la etiqueta viewport. Usted sigue mirando con los ojos.',
            'Captura en el teléfono y captura en el escritorio',
            {
              dependencyNote: 'Revise el diseño después de que la página realmente responda.',
              actionSteps: [
                'Abra el sitio en un teléfono, o encoja el navegador hasta que tenga el ancho de un teléfono.',
                'Puede leer las palabras sin deslizar de lado.',
                'Los botones son lo bastante grandes para tocarlos con un dedo.',
                'Abra la misma página en una pantalla ancha. Las mismas palabras siguen ahí.',
                'Si el escaneo dice que falta la etiqueta viewport, pida a quien construye el sitio que la agregue. Luego mire otra vez.',
              ],
            },
          ),
          'site-seo-basics': item(
            'La búsqueda puede encontrar la página',
            'Cada página tiene un título en la pestaña del navegador, un encabezado principal, una descripción de una frase, un sitemap y un archivo robots. El escaneo marca un hueco cuando faltan el título, la descripción o el encabezado único. No posiciona el sitio.',
            'Título de la portada, /sitemap.xml y /robots.txt',
            {
              sourceLabel: 'Central de Búsqueda de Google',
              dependencyNote: 'Los archivos de búsqueda se revisan después de que la página responde.',
              actionSteps: [
                'Abra la portada. La pestaña del navegador nombra el sitio.',
                'La página tiene un encabezado principal.',
                'Abra /sitemap.xml y /robots.txt en el mismo sitio. Ambos deben abrir.',
                'Cada página importante tiene una descripción de una frase.',
                'El escaneo marca un hueco cuando faltan el título, la descripción o el encabezado único. No lo coloca en Google.',
              ],
            },
          ),
          'site-hipaa-scope': item(
            'Los datos de salud no van en etiquetas de anuncios',
            'Si este sitio no recoge información de salud, escriba “No es un sitio de salud” y márquelo hecho. Si la recoge, publique un Aviso de prácticas de privacidad y no ponga etiquetas de anuncios o analítica en esas páginas salvo que el proveedor haya firmado un acuerdo de socio comercial.',
            'Nota “No es un sitio de salud”, o la URL del aviso más el acuerdo del proveedor',
            {
              sourceLabel: 'Aviso de prácticas de privacidad de HHS',
              dependencyNote: 'La página de privacidad se nombra antes de decidir si aplican las reglas de salud.',
              actionSteps: [
                'Pregunte: ¿este sitio recoge información de salud, como citas, síntomas, seguro o expedientes médicos?',
                'Si no, escriba “No es un sitio de salud” y márquelo hecho.',
                'Si sí, publique un Aviso de prácticas de privacidad y enlácelo desde el pie.',
                'Quite las etiquetas de anuncios y analítica de cualquier página que muestre información de salud, salvo que ese proveedor haya firmado un acuerdo de socio comercial.',
                'Esos formularios usan HTTPS y no ponen datos de salud en la dirección web.',
              ],
            },
          ),
          'site-pii-forms': item(
            'Los formularios no ponen datos personales en la dirección',
            'Los formularios de contacto, alta y pago quedan en HTTPS. La barra de dirección no contiene el nombre, el correo o el teléfono que escribió el visitante. Anote cuánto tiempo guarda las respuestas y quién puede verlas.',
            'Captura de un formulario en HTTPS, con la barra de dirección vacía después de enviar',
            {
              dependencyNote: 'Active el candado (HTTPS) antes de probar los formularios.',
              actionSteps: [
                'Abra cada formulario: contacto, alta y pago.',
                'Escriba un nombre y un correo de prueba. Mire la barra de dirección. No debe contener ese nombre ni ese correo.',
                'La dirección empieza con https://.',
                'Escriba cuánto tiempo guarda las respuestas y quién puede verlas.',
                'Guarde una captura de un formulario. No ponga un número de Seguro Social real en la captura.',
              ],
            },
          ),
          'site-no-cui-on-public': item(
            'Sin archivos de gobierno en el sitio público',
            'Este sitio es público. No suba aquí archivos de contrato, diagramas de sistemas ni CUI. Guárdelos en el proyecto de gobierno. Escriba “Solo sitio público” cuando no haya nada sensible.',
            'Nota “Solo sitio público”, o la ubicación de los archivos en el proyecto de gobierno',
            {
              actionSteps: [
                'Este sitio es para todas las personas en internet.',
                'No suba aquí archivos de contrato, diagramas de sistemas de gobierno ni CUI.',
                'Si un cliente necesita esos archivos, guárdelos en el proyecto de gobierno, no en este sitio.',
                'Escriba “Solo sitio público” cuando no haya nada sensible hospedado aquí.',
              ],
            },
          ),
        },
      },
      'privacy-data-governance': {
        label: 'Privacidad y gobernanza de datos',
        summary: 'El consentimiento, las divulgaciones, el inventario de cookies/rastreadores, Consent Mode y los derechos de las personas deben quedar transparentes y listos para el regulador bajo GDPR, CCPA/CPRA, COPPA y reglas similares.',
        items: {
          'privacy-policy-published': item(
            'Privacidad / cookies / términos publicados y enlazados',
            'Confirme que la Política de privacidad, la de cookies y los Términos actuales están enlazados desde el pie y los flujos de alta, con categorías, fines y compartición descritos.',
            'URL + fecha de última revisión',
            {
              docsLabel: 'Sitio oficial de GDPR',
              verifySteps: {
                'privacy-url': {
                  text: 'Conserve la URL viva de la política de privacidad y confirme que la página nombra los datos que recoge, por qué y con quién los comparte.',
                  evidenceHint: 'URL viva de la política de privacidad',
                },
              },
            },
          ),
          'cookie-consent-controls': item(
            'Controles de consentimiento de cookies / rastreo',
            'Verifique que las rutas de opt-in u opt-out funcionen antes de que disparen cookies no esenciales; registre la elección. Honre Global Privacy Control / No vender ni compartir donde se exija. Prefiera un centro de preferencias a un banner solo de aceptar/rechazar.',
            'Captura del banner + muestra del registro de consentimiento',
            { dependencyNote: 'Publique la página de privacidad antes de la elección de cookies, para que el banner pueda señalarla.' },
          ),
          'cookie-inventory-classified': item(
            'Inventario de cookies / rastreadores clasificado',
            'Mantenga un inventario categorizado de cookies y rastreadores propios y de terceros (necesarias, funcionales, analítica, publicidad). El escaneo de portada lista hosts detectados; confirme categorías y proveedores tras cada reescaneo.',
            'Lista de rastreadores del escaneo + mapeo a la política de cookies',
          ),
          'consent-proof-and-script-blocking': item(
            'Registros de consentimiento y bloqueo de scripts',
            'Bloquee scripts no esenciales hasta el consentimiento. Guarde prueba (marca de tiempo, versión, elección, región). Vuelva a pedir consentimiento cuando cambie la lista de cookies o los fines.',
            'Muestra de script bloqueado + recibo de consentimiento',
          ),
          'google-consent-mode': item(
            'Google Consent Mode y banners geográficos',
            'Si corren etiquetas o anuncios de Google, conecte Consent Mode v2 (e IAB TCF donde haya anuncios en la UE). Use banners por región para que el opt-in GDPR y el opt-out CCPA no sean un único valor global.',
            'Configuración de consentimiento en Tag Manager + captura de región',
            {
              verifySteps: {
                'gcm-scan': {
                  text: 'Ejecute el escaneo de portada y anote si hay etiquetas o anuncios de Google.',
                  evidenceHint: 'Hallazgo del escaneo de Consent Mode',
                },
                'gcm-config': {
                  text: 'En Google Tag Manager, configure Consent Mode para que el almacenamiento de anuncios y analítica espere una elección.',
                  evidenceHint: 'Captura de consentimiento en Tag Manager',
                },
                'gcm-banner': {
                  text: 'Conserve una captura del banner de cookies en el sitio vivo.',
                  evidenceHint: 'Captura del banner',
                },
                'gcm-pref': {
                  text: 'Confirme que el enlace del centro de preferencias permite cambiar la elección después.',
                  evidenceHint: 'URL del centro de preferencias',
                },
                'gcm-rescan': {
                  text: 'Vuelva a escanear cuando el banner esté vivo y confirme que Consent Mode sigue en el hallazgo.',
                  evidenceHint: 'Marca del segundo escaneo',
                },
              },
            },
          ),
          'scheduled-rescans': item(
            'Reescaneos programados de cookies / políticas',
            'Elija semanal (CompliCore) o diario (CompliAdvantage) en el escaneo de portada. Cada corrida queda en el historial para que una etiqueta nueva no aparezca en silencio.',
            'Dueño de la cadencia + marcas de los últimos dos escaneos',
          ),
          'data-inventory': item(
            'Inventario de recolección de datos del sitio',
            'Mapee formularios, analítica y etiquetas de terceros que recogen datos personales y anote base legal / finalidad, retención y encargados.',
            'Hoja de inventario o página Confluence',
          ),
          'dsar-workflow': item(
            'Flujo de derechos de la persona (DSAR)',
            'Confirme la ruta de recepción para acceso, corrección, supresión, portabilidad y opt-out con dueño y SLA (GDPR / CCPA/CPRA y otras leyes estatales en alcance).',
            'URL del runbook + ticket de muestra',
            {
              dependencyNote: 'No vender y Limitar información personal sensible de California quedan bloqueados hasta que la política de privacidad cumpla.',
              verifySteps: {
                'do-not-sell': {
                  text: 'Confirme que el enlace de No vender ni compartir mi información personal está en el sitio y funciona.',
                  evidenceHint: 'URL de No vender',
                },
                'limit-spi': {
                  text: 'Confirme que el enlace de Limitar el uso de mi información personal sensible está en el sitio y funciona.',
                  evidenceHint: 'URL de Limitar información sensible',
                },
              },
            },
          ),
          'coppa-audience': item(
            'Determinación de audiencia COPPA / menores de 13',
            'Registre si el sitio se dirige a menores de 13 o recolecta a sabiendas sus datos. Si sí, implemente aviso, consentimiento parental y un control de edad. Si no, conserve la determinación N/A con evidencia de audiencia.',
            'Memo de audiencia + capturas del control de edad o firma N/A',
          ),
        },
      },
      'accessibility-user-experience': {
        label: 'Accesibilidad y experiencia de usuario',
        summary: 'Revisiones de la Sección 508 y WCAG 2.1 nivel AA para las páginas públicas. Estas revisiones están en cada suscripción. Anote lo que encuentre. Esta página no certifica el sitio. Un widget overlay no sustituye el código.',
        items: {
          'keyboard-navigation': item(
            'Teclado, foco y enlace para saltar',
            'En la portada, el alta y el pago o consentimiento: llegue a cada control con el teclado, salga de cada control, vea dónde está el foco y use un enlace para saltar al contenido principal. WCAG 2.1 AA 2.1.1, 2.1.2, 2.4.1, 2.4.3 y 2.4.7. La Sección 508 usa esta barra para páginas web.',
            'Notas de teclado de cada plantilla',
          ),
          'alt-text-contrast': item(
            'Alternativas de texto, encabezados y contraste',
            'Dé una alternativa de texto a imágenes, iconos y gráficos. Use encabezados reales en orden. El contraste del texto es al menos 4.5:1, y el texto grande y los gráficos con significado al menos 3:1. No use el color como única señal. WCAG 2.1 AA 1.1.1, 1.3.1, 1.4.1, 1.4.3 y 1.4.11.',
            'Lista de páginas, lecturas de contraste y brechas',
          ),
          'form-labels': item(
            'Nombres de formularios, errores y mensajes de estado',
            'Cada campo tiene una etiqueta visible. Los errores nombran el campo y cómo corregirlo. Las acciones legales, financieras y de borrado se pueden revisar antes de enviarlas. Los mensajes de estado se anuncian sin mover el foco. El nombre, el rol y el valor del control están disponibles para un lector de pantalla. WCAG 2.1 AA 3.3.1, 3.3.2, 3.3.3, 3.3.4, 4.1.2 y 4.1.3.',
            'Notas del formulario, con un error y un mensaje de éxito',
          ),
          'wcag-media-captions': item(
            'Subtítulos y transcripciones',
            'Si una página tiene audio o video, agregue subtítulos del habla y una transcripción. El audio en vivo tiene subtítulos. Si no hay audio ni video, escriba “Sin medios” y márquelo hecho. WCAG 2.1 AA 1.2.1 a 1.2.5.',
            'Enlace de subtítulos o transcripción, o nota de Sin medios',
          ),
          'wcag-reflow-zoom': item(
            'Zoom, reflujo y espaciado del texto',
            'Al 200% de zoom las palabras siguen legibles. A 320 píxeles CSS de ancho la página refluye sin un desplazamiento lateral de las palabras. El espaciado de párrafos, la altura de línea y el espaciado de letras pueden aumentar sin cortar el texto. WCAG 2.1 AA 1.4.4, 1.4.10 y 1.4.12.',
            'Capturas al 200% y en ancho de teléfono',
          ),
          'wcag-motion-timing': item(
            'Tiempo, movimiento y contenido al pasar el cursor',
            'El visitante puede pausar, detener u ocultar lo que se mueve, parpadea o se actualiza solo. Nada destella más de tres veces en un segundo. El contenido que se abre al pasar el cursor o al enfocar se puede cerrar, recorrer y mantener abierto. Si hay un límite de tiempo, el visitante puede apagarlo, ajustarlo o extenderlo. WCAG 2.1 AA 1.4.13, 2.2.1, 2.2.2 y 2.3.1.',
            'Notas de carruseles, banners y ayudas, o N/A',
          ),
          'wcag-language-navigation': item(
            'Idioma de la página, enlaces y navegación constante',
            'La página nombra su idioma, y un pasaje en otro idioma está marcado. El propósito de cada enlace se entiende por su texto. La misma navegación y los mismos componentes aparecen en el mismo orden. Hay más de una forma de encontrar una página. WCAG 2.1 AA 2.4.4, 2.4.5, 2.4.6, 3.1.1, 3.1.2, 3.2.3 y 3.2.4.',
            'Etiqueta de idioma, enlaces de muestra y notas de navegación',
          ),
          'dark-pattern-review': item(
            'Revisión de patrones oscuros en consentimiento / flujos críticos',
            'Confirme que los flujos de consentimiento y cancelación sean claros; sin valores predeterminados engañosos ni opt-out obstruido (solapamiento FTC / ROSCA).',
            'Firma de la lista de revisión UX',
          ),
          'accessibility-statement-published': item(
            'Declaración de accesibilidad publicada',
            'Publique una declaración de accesibilidad actual que nombre WCAG 2.1 nivel AA, las brechas conocidas y un contacto. La declaración es una página, no una captura del overlay.',
            'URL de la declaración + fecha de última revisión',
          ),
          'overlay-not-wcag-substitute': item(
            'Los widgets overlay no son cumplimiento WCAG',
            'Si hay UserWay, accessiBe u overlays similares, regístrelos solo como ayuda. Mantenga las revisiones de código de esta lista. No marque este ítem revisado solo porque hay un overlay.',
            'Proveedor del overlay + fecha de la revisión de código, o N/A',
          ),
          'wcag-22-targets-and-focus': item(
            'Diseño de teléfono, tamaño del objetivo y un camino sin arrastrar',
            'En el ancho de un teléfono y al 200% de zoom, la página refluye en una dirección. Los controles enfocados se quedan en pantalla. Los botones y los enlaces miden al menos 24 por 24 píxeles, con espacio entre ellos. Lo que se puede arrastrar también tiene botones de subir y bajar u otro camino de clic. Revise la página de inicio, un formulario y un filtro. WCAG 2.2 añade 2.4.11, 2.5.7 y 2.5.8 sobre las revisiones de WCAG 2.1 de arriba. La Sección 508 sigue citando la barra anterior. Cumplir 2.2 es la práctica. Esta fila no certifica el sitio.',
            'Captura de teléfono, nota de zoom y la alternativa de clic para cualquier control que se arrastra',
          ),
          'framework-applicability': item(
            'Qué marcos de seguridad y privacidad aplican',
            'Escriba cuáles de estos aplican a este sitio y cuáles no: SOC 2, ISO 27001, ISO 27701, HIPAA, GDPR, CCPA/CPRA, PCI DSS, Sección 508, WCAG y EN 301 549. Un sitio de marketing sin datos de salud, sin números de tarjeta y sin clientes europeos no cubre todos. Vanta y herramientas parecidas vigilan muchos de estos para una empresa. Esta fila es la nota de aplicabilidad del sitio público. No certifica ningún marco y no reemplaza esas herramientas.',
            'Nota con fecha: en alcance, fuera de alcance y por qué',
          ),
          'section-508-conformance-record': item(
            'Registro de la Sección 508 frente a WCAG 2.1 AA',
            'Después de las revisiones de arriba, escriba las páginas que abrió, las brechas que siguen abiertas y a quién contactar. La Sección 508 usa WCAG 2.1 nivel AA para el contenido web. Si un comprador federal pide un VPAT o un ACR, guarde ese archivo con esta nota. No escriba “conforme” mientras una brecha de esas páginas siga abierta. Esta revisión está en cada suscripción. NextCompli no certifica el sitio.',
            'Lista de páginas, brechas abiertas, contacto, y un VPAT o ACR solo si un comprador lo pidió',
            { dependencyNote: 'Termine las revisiones de las páginas y publique la declaración de accesibilidad antes de escribir el registro de la Sección 508.' },
          ),
        },
      },
      'security-risk-protection': {
        label: 'Seguridad y protección del riesgo',
        summary: 'Transporte, encabezados, parches, contacto de incidentes y PCI del sitio público. CompliAdvantage añade un WAF y escaneos recurrentes; CompliComplete añade pruebas con profundidad ASVS.',
        items: {
          'https-transport': item(
            'HTTPS en páginas públicas y puntos críticos',
            'Confirme TLS 1.2+ en apex/www y que HTTP redirija a HTTPS; revise avisos de contenido mixto. El escaneo de portada registra si la URL final es HTTPS.',
            'Lista de URL + vencimiento TLS',
          ),
          'security-headers': item(
            'Línea base de encabezados de seguridad',
            'Revise HSTS, CSP (o un plan con fecha), X-Content-Type-Options y Referrer-Policy en los orígenes principales. El escaneo de portada marca encabezados faltantes.',
            'Salida del escaneo de encabezados',
            { dependencyNote: 'Active HTTPS antes de los encabezados extra. HSTS solo funciona en HTTPS.' },
          ),
          'dependency-patch-cadence': item(
            'Cadencia de vulnerabilidades / parches',
            'Confirme dueño y cadencia para parchear dependencias y CMS/plugins; anote el último ciclo completado.',
            'Fecha del último parche + dueño',
          ),
          'incident-contact': item(
            'Contacto público de seguridad / incidentes',
            'Publique o internalice un contacto de seguridad y una ruta de escalamiento para incidentes del sitio.',
            'Contacto + documento de escalamiento',
          ),
          'pci-if-payments': item(
            'Alcance PCI DSS si se aceptan tarjetas',
            'Si el sitio toma tarjetas, registre el tipo de SAQ PCI DSS, quién almacena PAN y que el checkout usa un procesador conforme. Si no hay tarjetas, registre N/A.',
            'Tipo de SAQ + atestación del procesador o nota N/A',
          ),
          'waf-recurring-scans': item(
            'WAF y escaneos recurrentes del sitio',
            'Ponga un WAF o filtro de borde equivalente frente a producción. Programe escaneos de encabezados/TLS/vulnerabilidades y envíe hallazgos al flujo de alertas de CompliAdvantage.',
            'Nombre del WAF + último job de escaneo + dueño de alerta',
          ),
          'asvs-depth-review': item(
            'Revisión de aplicación con profundidad ASVS',
            'Ejecute una revisión acotada contra OWASP ASVS Nivel 2 o 3 (authn, sesión, control de acceso, secretos). Archive el informe y las remediaciones en el espacio de auditoría con un consultor embebido.',
            'Informe ASVS + remediaciones tipo POA&M',
          ),
        },
      },
      'email-authenticity-deliverability': {
        label: 'Autenticidad y entregabilidad de correo',
        summary: 'Configure el buzón y luego SPF, DKIM y DMARC en ese orden. La clave DKIM es un solo registro. Partirla, o generarla y no iniciar la autenticación, es por lo que hay que arreglar el correo otra vez.',
        items: {
          'email-mailbox-mx': item(
            'Buzón e intercambio de correo en el dominio de la empresa',
            'Cree el buzón de la empresa y apunte el intercambio de correo del dominio a ese host. Edite el DNS solo en el panel que usan los servidores de nombres. Entre en el teléfono y en la app de correo con el acceso real, no con un alias de envío.',
            'Registro de intercambio de correo y un acceso que puede enviar',
            {
              actionSteps: [
                'Encuentre los servidores de nombres del dominio. Abra ese panel DNS. Un registro guardado en otro lugar no hace nada.',
                'Ponga el intercambio de correo en el host del buzón. Para Google Workspace es prioridad 1 y host smtp.google.com.',
                'Quite un host de correo viejo, como WorkMail, para que el correo nuevo no se divida.',
                'Cree el buzón de la empresa. En el teléfono o la app de correo, entre con ese acceso. Un alias de envío no es la cuenta.',
              ],
            },
          ),
          'email-spf-published': item(
            'SPF publicado en cada dominio que envía',
            'Publique un registro de texto SPF por dominio de envío que liste cada servicio que envía como ese dominio. Si el buzón es Google, el registro debe incluir _spf.google.com aunque Amazon u otro proveedor también envíe. Un registro que solo nombra al otro proveedor hace fallar el correo de las personas. Mantenga las búsquedas DNS por debajo de 10. El escaneo lee el registro de texto del dominio y lo compara con el intercambio de correo.',
            'Un registro de texto SPF y la lista de remitentes',
            {
              actionSteps: [
                'Liste cada sistema que envía como el dominio: el buzón de las personas y cualquier app o herramienta de marketing.',
                'Publique un registro de texto en el dominio. Empieza con v=spf1. No publique un segundo registro SPF. Dos registros SPF hacen fallar ambos.',
                'Si el buzón es Google, incluya _spf.google.com. Un registro que solo nombra a Amazon u otro remitente hace fallar el correo de las personas.',
                'Incluya cada otro remitente del mismo modo. Mantenga el número de búsquedas por debajo de 10. Termine con ~all.',
              ],
            },
          ),
          'email-dkim-signing': item(
            'Firma DKIM en el buzón',
            'Genere la clave DKIM en la administración del correo, publíquela como un registro de texto DNS y luego inicie la autenticación. Dos registros de texto con el mismo nombre no se unen y la firma falla. Generar la clave sin iniciar la autenticación no firma el correo. El escaneo comprueba nombres comunes (google, selector1, s1, default). No pulsa Iniciar autenticación por usted.',
            'Un registro de texto DKIM y la fuente de un mensaje firmado',
            {
              actionSteps: [
                'En Google Workspace abra Admin, luego Apps, Google Workspace, Gmail, Autenticar correo. Elija el dominio de la empresa. Pulse Generar registro nuevo.',
                'Anote el nombre de host. Para Google es google._domainkey. El valor es una clave larga.',
                'En el panel DNS que usan los servidores de nombres, añada un registro de texto con ese nombre. Varios fragmentos entre comillas dentro de un registro se unen. Dos registros de texto separados con el mismo nombre no se unen, y DKIM falla. No añada un segundo registro.',
                'Si otro sistema también envía como este dominio, como Amazon SES, añada las claves que ese sistema muestra, con los nombres que muestra. No pegue esas claves en google._domainkey.',
                'Vuelva a Autenticar correo y pulse Iniciar autenticación. Espere hasta que diga que está autenticando. El DNS puede tardar una hora. Una clave generada que nunca se inició no firma el correo.',
                'Microsoft 365 es distinto: publique los dos registros de alias que muestra el centro de administración (selector1 y selector2) y luego active la firma. No invente un registro de texto de Google para un buzón de Microsoft.',
              ],
            },
          ),
          'email-dmarc-monitor': item(
            'DMARC en p=none con informes agregados',
            'Publique un registro de texto _dmarc en p=none con un buzón de informes que alguien vaya a ver. Gmail, Yahoo y Microsoft tratan el correo sin política DMARC como más fácil de archivar en spam aunque SPF y DKIM pasen. No pase a cuarentena o rechazo hasta que un mensaje de prueba muestre que SPF y DKIM pasan. El escaneo marca esto hecho solo cuando están publicados la política y el buzón de informes.',
            'Registro DMARC y un buzón de informes que una persona lee',
            {
              actionSteps: [
                'En el mismo panel DNS, añada un registro de texto llamado _dmarc.',
                'Empiece con v=DMARC1; p=none; rua=mailto: y un buzón que una persona ya revisa.',
                'No ponga cuarentena o rechazo el primer día. Espere hasta que un mensaje de prueba muestre que SPF y DKIM pasan.',
              ],
            },
          ),
          'email-auth-check': item(
            'Lea un mensaje enviado y confirme que SPF y DKIM pasaron',
            'Envíe desde la dirección de la empresa a un buzón que pueda abrir. Muestre el original. SPF y DKIM deben pasar para el dominio de la empresa. Un fallo significa que el DNS está en el panel equivocado, que la clave DKIM se partió en dos registros, o que la autenticación nunca se inició.',
            'Original del mensaje con spf=pass y dkim=pass',
            {
              actionSteps: [
                'Envíe un mensaje desde la dirección de la empresa a una cuenta de Gmail que pueda abrir.',
                'Abra el mensaje y luego Mostrar original.',
                'Busque spf=pass y dkim=pass para el dominio de la empresa. fail significa volver: panel DNS equivocado, DKIM partido en dos registros, o Iniciar autenticación nunca se pulsó.',
                'Guarde ese original. Esa es la prueba. No marque los pasos anteriores como hechos solo con una captura de DNS.',
              ],
            },
          ),
          'email-inbox-action-note': item(
            'Nota de acción cuando el correo exige una decisión',
            'No obligue a un operador a vivir en el buzón de envío. En un horario que siga corriendo cuando su portátil está cerrado: envíe a la papelera el correo que es claramente spam, marque como leído el correo ordinario y mande una nota en lenguaje claro a un buzón que ya revisa cuando un mensaje exige una decisión (seguridad, pago o legal), con qué debe hacer. Mantenga los informes DMARC en ese mismo flujo. Use reglas fijas. No envíe el buzón a un modelo.',
            'Una nota de acción de ejemplo y la regla que mandó el spam a la papelera',
          ),
          'email-dmarc-quarantine': item(
            'Cuarentena DMARC y vigilancia de quejas',
            'Pase DMARC a p=quarantine cuando la alineación SPF/DKIM esté limpia. Mantenga la tasa de quejas de Gmail por debajo de 0,3% (objetivo bajo 0,1%). Envíe fallos de autenticación a las alertas de CompliAdvantage.',
            'Registro p=quarantine + captura de tasa de quejas',
          ),
          'email-one-click-unsub': item(
            'Baja de un clic y subdominios de envío',
            'El correo de marketing debe soportar List-Unsubscribe RFC 8058 de un clic (obligatorio por encima de 5.000/día hacia Gmail). Separe marketing y transaccional en subdominios dedicados para que una campaña no tumbe el correo de restablecer contraseña.',
            'Muestra del encabezado List-Unsubscribe + mapa de subdominios',
          ),
          'email-dmarc-reject-bimi': item(
            'DMARC reject y marca BIMI',
            'Aplique p=reject en los dominios de envío de producción. Para marcas de consumidor, publique BIMI con un Verified Mark Certificate cuando DMARC ya esté enforced. Revise rua/ruf de cada dominio con un consultor embebido.',
            'Registro p=reject + SVG/VMC BIMI o N/A documentado',
          ),
        },
      },
      'zero-trust-system-hardening': {
        label: 'Zero Trust y endurecimiento del sistema',
        summary: 'No confíe por defecto en la nube, contenedores, sitios o git. CompliCore es higiene esencial; CompliAdvantage monitorea controles IG2; CompliComplete gobierna IG3 / CISA óptimo.',
        items: {
          'zt-identity-mfa': item(
            'MFA en nube, git y administración del sitio',
            'Exija MFA en el pagador/root de la nube, el CMS/hosting del sitio y la org de git. Desactive autenticación heredada/básica. Esta es la primera función de identidad Zero Trust de CISA (verificar de forma explícita).',
            'Captura de la política MFA del IdP + lista de excepciones',
            {
              actionSteps: [
                'Abra la cuenta de nube, el host del sitio y la organización de git.',
                'Active MFA para cada persona que pueda cambiar el sitio.',
                'Apague el inicio de sesión solo con contraseña.',
                'Guarde una captura del ajuste de MFA. No pegue contraseñas aquí.',
              ],
            },
          ),
          'cloud-account-inventory': item(
            'Inventario de nube y hosting del sitio',
            'Liste cada cuenta de nube, región y origen público que sirve a este cliente (AWS, Azure, GCP, Vercel, DNS). Nombre un dueño. CIS Control 1 empieza por conocer los activos.',
            'Hoja de inventario + dueño + fecha de última revisión',
          ),
          'git-2fa-private-no-secrets': item(
            'Git: 2FA, repos privados, sin secretos en el historial',
            'Exija 2FA en la org de git. Mantenga los repos de producto privados salvo que sean públicos a propósito. Active el escaneo de secretos; rote lo que ya esté committed. Registre la URL de la org.',
            'Ajuste 2FA de la org + estado del escaneo de secretos',
          ),
          'code-lint-before-deploy': item(
            'El linter debe pasar antes de un deploy',
            'Active el linter del proyecto para que un fallo detenga el deploy. Guarde la fecha de la última corrida en verde. El escaneo de portada no puede leer código privado.',
            'Fecha de la última corrida de lint en verde y el nombre de la herramienta',
            {
              dependencyNote: 'Cierre el repositorio (2FA, privado, sin secretos) antes de confiar en la puerta del linter.',
              actionSteps: [
                'Abra el repositorio de código de este sitio.',
                'Active el linter para que un fallo detenga el deploy.',
                'Ejecútelo. Corrija las líneas rojas. Una corrida en verde es la prueba.',
                'Escriba aquí la fecha de esa corrida en verde. El escaneo de portada no ve el código privado.',
              ],
            },
          ),
          'container-no-secrets-nonroot': item(
            'Contenedores: sin secretos en la imagen, usuario no root',
            'Si el sitio envía contenedores, confirme que las imágenes no tienen contraseñas/tokens embebidos (guía DoD de contenedores 2.18) y no corren como root. Si no usa contenedores, archive N/A con el modelo de hosting.',
            'Línea USER del Dockerfile + nota del almacén de secretos o N/A',
          ),
          'zt-cloud-cis-logging': item(
            'CIS Benchmark de nube y bitácoras de auditoría',
            'Ejecute un CIS Benchmark (o equivalente) contra la cuenta de nube de producción. Conserve CloudTrail / Activity Log / Cloud Audit Logs inmutables y alerte cambios de IAM y almacenamiento público.',
            'Informe del referente + prueba del destino de logs',
          ),
          'git-protected-secret-scan': item(
            'Git: ramas protegidas y protección al push',
            'Exija revisión de pull request en las ramas por defecto, CODEOWNERS donde haga falta, y escaneo de secretos con push protection para que las credenciales no lleguen a main.',
            'Captura de protección de rama + activación del escaneo de secretos',
          ),
          'container-image-scan-sbom': item(
            'Escaneo de imagen de contenedor y SBOM',
            'Escanee imágenes en CI por vulnerabilidades conocidas (Trivy/Grype/equivalente). Produzca un SBOM por release. Bloquee deploys de críticos sin remediar según política.',
            'Log de escaneo CI + URL del artefacto SBOM',
          ),
          'cloud-cspm-workload-identity': item(
            'CSPM, identidad de carga y segmentación de red',
            'Corra CSPM continuo. Reemplace claves de nube de larga vida por identidad de carga. Segmente redes de producción (sin VPC plana). Mapee a los pilares Networks + Applications de CISA Zero Trust.',
            'Tablero CSPM + ejemplo de identidad de carga + diagrama de red',
          ),
          'supply-chain-signing-sso': item(
            'Imágenes firmadas, SSO de git y auditoría inmutable',
            'Firme imágenes de contenedor y verifique en admisión. Exija SSO/SAML en git con baja SCIM. Conserve logs inmutables de git y CI. Los consultores embebidos revisan trimestralmente contra CIS IG3.',
            'Política de admisión + SSO del IdP + última revisión trimestral',
          ),
        },
      },
      'ai-ml-zero-trust-guardrails': {
        label: 'IA/ML Zero Trust y barreras',
        summary:
          'Trate cada chat, asistente o función generativa pública como entrada no confiable y salida de modelo no confiable. Intercepte prompts tóxicos, políticos/fuera de tema e inyecciones antes de que lleguen al LLM. Este pilar guarda dueños y prueba. No es un certificado ISO/IEC 42001.',
        items: {
          'web-ai-feature-inventory': item(
            'Inventario de chats y asistentes de IA públicos',
            'Liste cada función de IA generativa en este sitio (chat de soporte, asistente de búsqueda, ayuda en formularios, voz, imagen). Para cada una, escriba el proveedor o familia de modelo, qué páginas la muestran, de quién es la cuenta que paga y quién puede apagarla. Si el sitio no tiene IA generativa, archive una nota fechada que lo diga. No pegue aquí prompts de clientes.',
            'Filas de inventario (o nota fechada “sin IA pública”) con dueño e interruptor',
            {
              actionSteps: [
                'Recorra el sitio público y anote cada widget de chat, asistente o control “preguntar a la IA”.',
                'Para cada uno, escriba el nombre del producto o familia de modelo y la URL de la página.',
                'Nombre a la persona que puede desactivarlo.',
                'Si no hay IA generativa, guarde una nota fechada que diga ninguna.',
              ],
            },
          ),
          'web-ai-input-rails': item(
            'Bloquee prompts inseguros antes de que lleguen al LLM',
            'Ponga un riel de entrada delante de cada función generativa pública para revisar el mensaje del usuario antes de que corra el modelo principal. Bloquee o reescriba contenido tóxico, intentos claros de inyección de prompts y pedidos fuera de su lista de temas permitidos. Pilas de código abierto aceptables incluyen rieles de entrada de NVIDIA NeMo Guardrails con Meta Llama Guard (o Llama Prompt Guard) para categorías de seguridad, o un filtro hospedado equivalente. Solo regex estático no basta para CompliAdvantage. Registre qué capa corre primero y un ejemplo bloqueado (sin texto privado).',
            'Nota de arquitectura que nombra el riel de entrada + una muestra bloqueada anonimizada',
            {
              dependencyNote: 'Sepa qué funciones de IA pública existen antes de probar el riel de entrada.',
              actionSteps: [
                'Confirme que cada ruta de IA pública pasa por un filtro previo al LLM (no directo a la API del modelo).',
                'Active comprobaciones de toxicidad / categorías inseguras (Llama Guard, NeMo Guardrails o equivalente).',
                'Active detección de inyección de prompts (reglas más un clasificador como Llama Prompt Guard o equivalente).',
                'Guarde una nota corta con los nombres de producto y una muestra bloqueada sin texto privado.',
              ],
              sourceLabel: 'Rieles de protección NVIDIA NeMo',
              docsLabel: 'Top 10 de OWASP para aplicaciones LLM',
            },
          ),
          'web-ai-topic-boundary': item(
            'Los pedidos políticos y fuera de tema quedan fuera de alcance',
            'Publique una lista de temas permitidos para cada asistente público (por ejemplo: ayuda del producto, precios, cómo usar la cuenta). Rechace política, religión, consejo médico, consejo legal y otros pedidos fuera de tema con un mensaje fijo de redirección. Modelos de control de tema (por ejemplo NeMo Topic Control) o un clasificador equivalente sirven. El rechazo debe ocurrir en el riel de entrada, no después de una respuesta larga del modelo.',
            'Lista de temas permitidos + muestra de rechazo fuera de tema (sin texto privado)',
            {
              dependencyNote: 'El riel de entrada debe existir antes de probar el rechazo por tema.',
              sourceLabel: 'Control de temas de NeMo',
            },
          ),
          'web-ai-output-rails': item(
            'Filtre la salida del modelo antes de que llegue al visitante',
            'Después de que el modelo responda, ejecute un riel de salida que bloquee fuga de datos privados, lenguaje tóxico e instrucciones que empujen a un visitante a hacer algo dañino. Prefiera la misma pila de seguridad que el riel de entrada (Llama Guard check output en NeMo Guardrails, o equivalente). Registre los rechazos. Una persona sigue siendo dueña de las respuestas de alto riesgo.',
            'Nombre del filtro de salida + una muestra bloqueada o reescrita',
            {
              dependencyNote: 'Alinee el riel de salida con las mismas rutas de IA pública que el riel de entrada.',
              sourceLabel: 'Top 10 de OWASP para aplicaciones LLM',
            },
          ),
          'web-ai-governance-eval': item(
            'Gobierne la ruta de IA: dueño, evaluación y nunca confiar solo',
            'Nombre un dueño para cada función de IA pública. Mantenga una regla corta de uso aceptable. Una vez por trimestre (o antes de un cambio mayor de modelo), ejecute un conjunto pequeño de pruebas de rechazo: muestras tóxicas, políticas/fuera de tema y “ignore previous instructions”. Registre pasa/falla y latencia. Zero Trust significa que el modelo nunca se confía solo: autenticación, mínimo privilegio en herramientas y escalamiento humano permanecen. Cruce con la consola de Cumplimiento de IA para el inventario de toda la empresa.',
            'Dueño + nota de evaluación fechada + enlace o puntero al inventario de Cumplimiento de IA',
            {
              dependencyNote: 'Pruebe los rieles de entrada y de tema antes de la evaluación trimestral.',
              docsLabel: 'Abrir Cumplimiento de IA',
            },
          ),
        },
      },
      'intellectual-property-dmca': {
        label: 'Propiedad intelectual y DMCA',
        summary: 'Avisos de derechos de autor, agente designado y proceso de retirada DMCA § 512, y atribución de licencias de terceros.',
        items: {
          'copyright-notice': item(
            'Avisos de derechos de autor y legales publicados',
            'Publique un aviso de derechos de autor y una declaración de titularidad actuales en páginas públicas (pie o legal). Mantenga el año y el titular exactos.',
            'Captura del pie + fecha de última revisión',
          ),
          'dmca-designated-agent': item(
            'Agente designado DMCA registrado',
            'Registre un agente designado ante la Oficina de Derechos de Autor de EE.UU. y publique nombre, dirección postal, teléfono y correo en el sitio (17 U.S.C. § 512).',
            'Listado en el directorio copyright.gov + datos del agente en el sitio',
            { docsLabel: 'Texto de 17 U.S.C. § 512' },
          ),
          'dmca-intake-workflow': item(
            'Flujo de avisos y contraavisos DMCA',
            'Publique cómo enviar un aviso de retirada § 512(c) y un contraaviso. Nombre al dueño, el SLA de respuesta y dónde vive el registro del caso. Simule el proceso al menos una vez al año.',
            'URL de la página DMCA + runbook + fecha del último simulacro',
          ),
          'repeat-infringer-oss': item(
            'Política de infractores reincidentes y atribución OSS',
            'Adopte una política de infractores reincidentes para contenido de usuarios o alojado. Inventarie las licencias de código abierto enviadas y conserve las atribuciones/avisos exigidos con el producto.',
            'URL de la política + página SBOM / atribución',
          ),
        },
      },
      'marketing-terms-consumer-rules': {
        label: 'Marketing, términos y reglas al consumidor',
        summary: 'Exigibilidad del clickwrap, publicidad veraz, renovación automática/cancelación, correo y límites de uso aceptable.',
        items: {
          'clickwrap-assent': item(
            'Asentimiento clickwrap / E-SIGN a los términos',
            'Exija asentimiento afirmativo a los Términos vigentes antes del alta. Guarde cuenta, marca de tiempo y versión de los términos para que los registros electrónicos sean reproducibles (15 U.S.C. § 7001).',
            'Captura del alta + muestra del registro de aceptación',
          ),
          'claims-substantiation': item(
            'Controles de publicidad y endorsements FTC',
            'Sustente las afirmaciones de marketing públicas antes de publicar. Divulgue conexiones materiales en testimonios (16 C.F.R. Part 255). Mantenga un registro de afirmación-a-evidencia.',
            'Registro de afirmaciones + fecha de última revisión legal',
            { docsLabel: 'Guías de respaldos de la FTC' },
          ),
          'billing-negative-option': item(
            'Renovación automática, cancelación y divulgación del costo total',
            'Muestre precio, renovación y una ruta simple de cancelación antes de cobrar (ROSCA / 16 C.F.R. Part 425). Haga coincidir el texto del checkout con la sección de facturación de los Términos.',
            'Capturas de checkout + flujo de cancelación',
          ),
          'canspam-aup': item(
            'CAN-SPAM, AUP y límites de uso prohibido',
            'El correo comercial debe identificar al remitente, incluir una baja que funcione y honrar las exclusiones. Publique reglas de uso aceptable / prohibido y una ruta de escalamiento. Empareje este ítem legal con el pilar de autenticidad de correo (SPF/DKIM/DMARC) para que el mail sea lícito y se entregue.',
            'Muestra del pie de correo + URL de AUP + última prueba de baja',
          ),
        },
      },
    },
  },
  'education-compliance': {
    title: 'Cumplimiento educativo',
    subtitle:
      'Dueños de programa del campus para sanciones, exportación de investigación, proveedores, privacidad estudiantil, seguridad, Título IX, accesibilidad, integridad de la investigación y el calendario federal. Los expedientes y los registros educativos siguen en los sistemas del campus que ya los guardan.',
    notices: {
      'not-maxient': {
        title: 'No es un sistema de casos de asuntos estudiantiles',
        body: 'Nombre al dueño del programa, la política y el calendario. Los casos de conducta, las investigaciones de Título IX, el registro de delitos Clery y los expedientes educativos siguen en las herramientas que esas oficinas ya usan.',
      },
      'hecvat-license': {
        title: 'HECVAT permanece en EDUCAUSE',
        body: 'Enlace el kit oficial HECVAT 4. No pegue preguntas EDUCAUSE en NextCompli. Los motores GRC de terceros que puntúan HECVAT aún necesitan licencia EDUCAUSE.',
      },
    },
    pillars: {
      'restricted-party-screening': {
        label: 'Cribado de partes restringidas del campus',
        summary: 'Criba a visitantes, MOU, universidades extranjeras y envíos antes de que avance la colaboración; mantenga a las partes despejadas en monitoreo.',
        items: {
          'education-compliance-calendar': item(
            'Ponga las fechas de este trabajo en el calendario',
            'Abra el calendario de esta página y guarde una fecha para IPEDS, el reporte anual de seguridad y el reporte de regalos extranjeros. Elija No este año solo cuando esa fila no aplique. No invente una fecha.',
            'Cada fecha que aplica, guardada en el calendario',
            {
              completeLabel: 'Marcar completo (fechas en el calendario)',
              verifySteps: {
                ipeds: {
                  text: 'Guarde la fecha de la colección IPEDS, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
                clery: {
                  text: 'Guarde la fecha del reporte anual de seguridad, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
                'foreign-gift': {
                  text: 'Guarde la fecha del reporte de regalos extranjeros, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
              },
            },
          ),
          'edu-rps-scholars': item(
            'Cribar académicos visitantes, empleados e invitados',
            'Ejecute cribado OFAC SDN / CAPTA de personas nombradas antes de acceso al campus, nombramiento o estipendio. Registre el propósito (académico, empleado, conferencista invitado).',
            'ID de caso OFAC + propósito + fecha de decisión',
            { docsLabel: 'Abrir cribado OFAC' },
          ),
          'edu-rps-mous': item(
            'Cribar MOU, universidades extranjeras y socios de investigación',
            'Criba la entidad legal y alias conocidos en cada nuevo MOU, doble titulación o alianza de investigación patrocinada antes de firmar.',
            'Paquete de cribado de entidad + URL del expediente MOU',
            { docsLabel: 'Abrir cribado OFAC' },
          ),
          'edu-rps-shipments': item(
            'Cribar envíos de exportación y equipo de investigación',
            'Criba consignatarios, transitarios y usuarios finales antes de que instrumentos, muestras o software salgan del campus. Escale posibles hallazgos BIS / DDTC.',
            'Cribado de envío + nota ECCN / licencia si hay',
            { docsLabel: 'Abrir cribado OFAC' },
          ),
          'edu-rps-monitoring': item(
            'Inscribir a las partes despejadas en monitoreo continuo',
            'Suscriba a académicos y socios que permanecen en campus o bajo un MOU activo a alertas de cambio de listas. No trate un despeje único como permanente.',
            'IDs de suscripción de monitoreo + cadencia de revisión',
            {
              docsLabel: 'Abrir feed en vivo OFAC',
              dependencyNote: 'Revise a la persona, al socio y el envío antes de ponerlos en la lista de vigilancia.',
            },
          ),
        },
      },
      'research-export-cui': {
        label: 'Exportación de investigación y CUI',
        summary: 'Clasifique trabajo público, FCI, CUI o controlado para exportación y reutilice la consola DoD para evidencia 800-171 / CMMC cuando una subvención o contrato lo exija.',
        items: {
          'edu-cui-classify': item(
            'Clasificar la actividad de investigación',
            'Registre si el proyecto es público, solo FCI, CUI o controlado para exportación antes de aprobar acceso de nacionales extranjeros o alojamiento en la nube.',
            'Decisión de clasificación + citas de cláusulas de contrato / subvención',
            { docsLabel: 'Abrir cumplimiento gubernamental' },
          ),
          'edu-cui-deemed-export': item(
            'Documentar acceso de nacionales extranjeros y deemed-export',
            'Liste quién puede llegar al laboratorio, enclave de datos o código fuente. Registre ciudadanía / estatus de visa solo como ya lo recaba la oficina de control de exportaciones.',
            'Roster de acceso + visto bueno de la oficina de exportaciones',
            { docsLabel: 'Abrir cumplimiento gubernamental' },
          ),
          'edu-cui-800-171': item(
            'Conservar evidencia 800-171 / CMMC cuando hay CUI',
            'Si aplica DFARS 252.204-7012 o una cláusula CMMC, complete la ruta SPRS / SSP de la consola DoD. No invente un segundo banco de CUI aquí.',
            'Empresa de la consola DoD + puntero SPRS o SSP',
            { docsLabel: 'Abrir cumplimiento gubernamental' },
          ),
          'edu-cui-country-alerts': item(
            'Vigilar destinos sancionados de forma integral',
            'Marque colaboraciones, viajes y envíos que involucren países sancionados de forma integral antes de que la oficina de investigación se comprometa.',
            'Registro de alerta de país + página del programa OFAC',
            { docsLabel: 'Abrir listas CAPTA OFAC' },
          ),
        },
      },
      'hecvat-vendor-trust': {
        label: 'HECVAT y confianza de proveedores',
        summary: 'Recoja paquetes HECVAT del campus de proveedores EdTech y mantenga actualizado el paquete de aseguramiento de NextCompli. No incruste el libro EDUCAUSE.',
        items: {
          'edu-hecvat-toolkit': item(
            'Usar el kit oficial EDUCAUSE HECVAT 4',
            'Descargue Full / Lite de EDUCAUSE. Guarde los libros completados de proveedores en el almacén de evidencia de la institución; no copie preguntas a este producto.',
            'Versión del kit (p. ej. 4.1.6) + ubicación de almacenamiento',
            { docsLabel: 'Kit HECVAT de EDUCAUSE' },
          ),
          'edu-hecvat-inventory': item(
            'Inventariar proveedores EdTech que tocan datos de estudiantes, ayudas o investigación',
            'Siga si cada proveedor tiene un HECVAT vigente, SOC 2, DPA y descripción de flujo de datos. Marque brechas antes de la renovación.',
            'URL del inventario de proveedores + fecha de última revisión',
          ),
          'edu-hecvat-nextcompli-pack': item(
            'Mantener actualizado el paquete de aseguramiento campus de NextCompli',
            'Revise el Trust Center público y el paquete campus antes de un RFP. Un HECVAT completado para un trato campus específico sigue asistido por ventas en v1.',
            'URL del paquete + fecha de última revisión',
            { docsLabel: 'Paquete de aseguramiento campus' },
          ),
          'edu-hecvat-trust-center': item(
            'Dirigir a revisores del campus a /trust',
            'Comparta el Trust Center público (marcos, contacto de seguridad, paquete campus) en lugar de volcados PDF ad hoc.',
            'URL del Trust Center en la respuesta al RFP',
            { docsLabel: 'Centro de confianza' },
          ),
        },
      },
      'ferpa-glba-program': {
        label: 'Programa FERPA y GLBA',
        summary: 'Nombre dueños para el aviso FERPA, el acceso de 45 días, el registro de divulgaciones y Salvaguardas GLBA. Esto es higiene de programa, no un portal de registro o ayudas.',
        items: {
          'edu-ferpa-notice': item(
            'Asignar al dueño del aviso anual FERPA',
            'Confirme quién publica el aviso anual FERPA y dónde lo encuentran los estudiantes. NextCompli no aloja registros educativos.',
            'Nombre del dueño + URL del aviso',
          ),
          'edu-ferpa-access': item(
            'Documentar la ruta de acceso a registros de 45 días',
            'Registre recepción, verificación de identidad y SLA para solicitudes de inspección de estudiantes elegibles / padres bajo 34 CFR Part 99.',
            'URL del runbook + ticket de muestra (sin PII estudiantil en esta consola)',
          ),
          'edu-ferpa-disclosure-log': item(
            'Tener un dueño del registro de divulgaciones 34 CFR 99.31',
            'Nombre la oficina que registra divulgaciones FERPA sin consentimiento. Capture la ubicación del registro, no su contenido.',
            'Dueño + nombre del sistema de registro',
          ),
          'edu-glba-wisp': item(
            'Mapear Salvaguardas GLBA para escuelas Title IV',
            'Las instituciones Title IV necesitan un programa escrito de seguridad de la información bajo 16 CFR 314. Señale el WISP y al dueño de supervisión de proveedores (HECVAT ayuda; no es toda la regla).',
            'URL del WISP + contacto ciber FSA',
          ),
          'edu-ferpa-breach': item(
            'Nombrar al dueño de una brecha de expedientes estudiantiles',
            'Registre quién decide un aviso FERPA o estatal, y dónde vive el registro de incidentes. No pegue nombres de estudiantes en esta consola.',
            'Dueño + nombre del sistema de incidentes',
          ),
        },
      },
      'campus-safety-program': {
        label: 'Programa de seguridad del campus',
        summary: 'Nombre dueños del informe Clery, los avisos oportunos y la prevención VAWA. El registro de delitos sigue en el sistema de seguridad del campus.',
        items: {
          'edu-clery-asr': item(
            'Asignar al dueño del informe anual de seguridad Clery',
            'Confirme quién publica el informe anual de seguridad y el de incendios, y la fecha en que estudiantes y empleados pueden encontrarlos.',
            'Dueño + URL del informe + fecha de publicación',
          ),
          'edu-clery-warnings': item(
            'Separar avisos oportunos de notificaciones de emergencia',
            'Nombre quién emite un aviso oportuno Clery y quién emite una notificación de emergencia. Registre la geografía que usa el campus. No copie el registro de delitos aquí.',
            'Nombres de dueños + nota de geografía',
          ),
          'edu-vawa-training': item(
            'Mantener el calendario de capacitación de prevención VAWA',
            'Registre la capacitación anual sobre violencia en el noviazgo, violencia doméstica, agresión sexual y acecho, y quién la recibe.',
            'URL del calendario + última fecha de cumplimiento',
          ),
          'edu-hazing-report': item(
            'Nombrar al dueño del informe de novatadas',
            'La ley de transparencia sobre novatadas añade un informe al paquete Clery. Nombre la oficina que lo publica y dónde vivirá.',
            'Dueño + ubicación del informe',
          ),
        },
      },
      'title-ix-program': {
        label: 'Programa de Título IX',
        summary: 'Nombre al coordinador, la política y el calendario de capacitación. Las investigaciones siguen en el sistema de casos del campus.',
        items: {
          'edu-tix-coordinator': item(
            'Publicar al coordinador de Título IX',
            'Registre el nombre, la oficina y la URL pública de la política. No guarde quejas ni nombres de partes aquí.',
            'Coordinador + URL de la política',
          ),
          'edu-tix-training': item(
            'Mantener vigente la capacitación de Título IX',
            'Registre el año de capacitación del coordinador, los investigadores y quienes deciden, y dónde se archivan los materiales.',
            'Año de capacitación + ubicación de materiales',
          ),
          'edu-tix-athletics': item(
            'Nombrar al dueño de equidad atlética',
            'Confirme quién presenta la divulgación de equidad en el atletismo y quién revisa participación y ayudas. Es un calendario, no una lista de jugadores.',
            'Dueño + último año presentado',
          ),
        },
      },
      'accessibility-civil-rights': {
        label: 'Accesibilidad y derechos civiles',
        summary: 'Nombre dueños de ADA, Sección 504 y acceso digital público. Los expedientes de ajustes siguen en la oficina de discapacidad.',
        items: {
          'edu-504-owner': item(
            'Nombrar al coordinador de Sección 504 y ADA',
            'Registre quién recibe solicitudes de ajuste y dónde está el aviso público. No guarde expedientes médicos aquí.',
            'Coordinador + URL del aviso',
          ),
          'edu-508-digital': item(
            'Asignar un dueño de accesibilidad del sitio público',
            'Nombre quién revisa páginas públicas y plataformas de cursos contra WCAG, y dónde se archiva la última revisión.',
            'Dueño + fecha de la última revisión',
          ),
          'edu-minors-program': item(
            'Nombrar al dueño de programas con menores',
            'Registre quién aprueba campamentos y programas que reciben menores, y dónde viven las reglas de verificación y supervisión.',
            'Dueño + URL de la política',
          ),
        },
      },
      'research-integrity': {
        label: 'Integridad de la investigación',
        summary: 'Nombre dueños de la revisión con personas, los conflictos y la política de mala conducta. Los expedientes de participantes siguen en el IRB.',
        items: {
          'edu-irb-owner': item(
            'Nombrar al dueño del IRB o de sujetos humanos',
            'Confirme qué junta revisa la investigación con personas y dónde se publica la política. No pegue protocolos ni listas de participantes.',
            'Nombre de la junta + URL de la política',
          ),
          'edu-coi-research': item(
            'Mantener la divulgación de conflictos de la investigación',
            'Nombre quién recoge las divulgaciones de investigadores antes de un premio federal, y la cadencia de revisión.',
            'Dueño + fecha de la última revisión',
          ),
          'edu-misconduct-policy': item(
            'Señalar la política de mala conducta en la investigación',
            'Registre la URL de la política y al oficial de integridad. Las denuncias siguen en el archivo de esa oficina.',
            'URL de la política + nombre del oficial',
          ),
        },
      },
      'federal-reporting-calendar': {
        label: 'Calendario de reportes federales',
        summary: 'Un calendario para IPEDS, regalos extranjeros, información al consumidor y autorización de educación a distancia.',
        items: {
          'edu-ipeds': item(
            'Asignar al dueño de la presentación IPEDS',
            'Nombre quién presenta cada encuesta IPEDS y dónde se siguen las fechas de cierre.',
            'Dueño + calendario de la colección vigente',
          ),
          'edu-section-117': item(
            'Nombrar al dueño del reporte de regalos extranjeros de la HEA',
            'La Sección 117 exige reportar ciertos regalos y contratos extranjeros. Registre quién los suma y la última presentación.',
            'Dueño + último periodo presentado',
          ),
          'edu-consumer-info': item(
            'Publicar al dueño de la información al consumidor',
            'Confirme quién mantiene vigente la página de información al consumidor de la HEA: precios, resultados y avisos exigidos.',
            'Dueño + URL de la página',
          ),
          'edu-distance-auth': item(
            'Registrar la autorización de educación a distancia',
            'Si la institución enseña a estudiantes fuera de su estado, nombre quién sigue la autorización estatal o la participación en NC-SARA.',
            'Estado de origen + nota de autorización o SARA',
          ),
        },
      },
      'program-governance': {
        label: 'Gobierno del programa',
        summary: 'Un comité con nombre, capacitación por rol y un informe al directorio. Así el campus muestra que el programa opera.',
        items: {
          'edu-committee': item(
            'Nombrar el comité de cumplimiento y su cadencia',
            'Registre quién se sienta, con qué frecuencia se reúne y dónde se guardan las actas. Las actas no deben contener expedientes estudiantiles.',
            'Presidencia + cadencia + ubicación de actas',
          ),
          'edu-training-matrix': item(
            'Mantener una matriz de capacitación por rol',
            'Mapee la capacitación de FERPA, Clery, Título IX, GLBA y exportación de investigación a los roles que la necesitan, con una fecha de renovación.',
            'URL de la matriz + año de renovación',
          ),
          'edu-board-report': item(
            'Programar el informe al directorio o al comité de auditoría',
            'Nombre quién reporta brechas abiertas, presentaciones vencidas y capacitación cumplida. El informe son conteos y dueños, no expedientes.',
            'Dueño del informe + fecha del último informe',
          ),
          'edu-hipaa-clinic': item(
            'Decir si una clínica del campus está en el alcance de HIPAA',
            'Si una clínica o un plan de salud guarda registros de tratamiento, nombre al oficial de privacidad y la política. Si no hay clínica cubierta, registre que el alcance es ninguno.',
            'En alcance o no + oficial de privacidad si aplica',
          ),
        },
      },
    },
  },
  'business-creation-compliance': {
    title: 'Formación de entidad legal',
    subtitle:
      'Primero el nombre de la entidad legal. Un negocio, una organización sin fines de lucro y un campus empiezan aquí. Luego una pregunta a la vez. Las respuestas se vuelven los pasos de presentación, impuestos, licencias y reportes.',
    notices: {
      'not-a-filing-service': {
        title: 'Una lista, no una mesa de presentación',
        body: 'Las preguntas escriben los pasos de esta empresa. NextCompli no envía artículos, una solicitud de EIN ni una licencia a una oficina del gobierno.',
      },
      'not-legal-advice': {
        title: 'Confirme la regla del estado antes de presentar',
        body: 'Las tarifas, los formularios y los plazos cambian según el estado. Revise el sitio del Secretario de Estado, o pregunte a un abogado, antes de dar un paso por terminado.',
      },
    },
    pillars: {
      'formation-calendar': {
        label: 'Fechas',
        summary: 'Ponga el reporte del estado y la renovación del agente registrado en este calendario. La entrevista sigue escribiendo los pasos de presentación.',
        items: {
          'business-creation-compliance-calendar': item(
            'Ponga las fechas de este trabajo en el calendario',
            'Abra el calendario de esta página y guarde una fecha para el reporte del estado y la renovación del agente. Elija No este año solo cuando esa fila no aplique. No invente una fecha.',
            'Cada fecha que aplica, guardada en el calendario',
            {
              completeLabel: 'Marcar completo (fechas en el calendario)',
              verifySteps: {
                'state-report': {
                  text: 'Guarde la fecha del reporte periódico del estado, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
                'agent-renewal': {
                  text: 'Guarde la fecha de renovación del agente registrado, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
              },
            },
          ),
        },
      },
    },
  },
  'wills-trusts-compliance': {
    title: 'Testamentos y fideicomisos',
    subtitle:
      'Primero ponga el nombre de la persona. Luego responda una pregunta a la vez. Las respuestas se convierten en la lista de un testamento o un fideicomiso revocable. Un abogado con licencia en ese estado redacta y revisa los papeles. NextCompli no.',
    notices: {
      'not-a-law-firm': {
        title: 'Una lista, no un bufete',
        body: 'Las preguntas escriben los pasos de esta persona. NextCompli no redacta un testamento ni un fideicomiso, no notariza una firma y no presenta nada ante un tribunal sucesorio.',
      },
      'lawyer-reviews': {
        title: 'Un abogado de ese estado revisa los papeles antes de que alguien firme',
        body: 'Las reglas de firma cambian según el estado. No copie la regla de testigos de otro estado. No firme hasta que un abogado con licencia en el estado de la persona haya revisado los papeles.',
      },
    },
    pillars: {
      'wills-calendar': {
        label: 'Fechas',
        summary: 'Ponga la cita de firma y la próxima revisión en este calendario. La entrevista sigue escribiendo los pasos del plan.',
        items: {
          'wills-trusts-compliance-calendar': item(
            'Ponga las fechas de este trabajo en el calendario',
            'Abra el calendario de esta página y guarde una fecha para cada fila que aplique. Elija No este año solo cuando esa fila no aplique. No invente una fecha. Las fechas de otro trabajo de cumplimiento quedan en sus propias páginas y también aparecen en el calendario de todos los proyectos.',
            'Cada fecha que aplica, guardada en el calendario',
            {
              completeLabel: 'Marcar completo (fechas en el calendario)',
              verifySteps: {
                'plan-review': {
                  text: 'Guarde la fecha de Volver a leer el plan, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
                'signing-appointment': {
                  text: 'Guarde la fecha de Cita de firma, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
              },
            },
          ),
        },
      },
    },
  },
  'cloud-compliance': {
    title: 'Cumplimiento de la nube',
    subtitle:
      'CompliCore es la lista escrita de la cuenta del cliente. CompliAdvantage añade una revisión de solo lectura una vez al día, después de que el cliente concede un rol estrecho de auditoría. Esta consola no usa la cuenta de operaciones de NextCompli y no recorre el sitio público.',
    notices: {
      'client-cloud-not-ops': {
        title: 'La cuenta de nube del cliente no es la cuenta de operaciones de NextCompli',
        body:
          'Cumplimiento del sitio web lee páginas públicas. Cumplimiento de la nube necesita el acceso que el cliente concede a su propia cuenta. No apunte esta consola a la cuenta donde corre NextCompli.',
      },
      'checklist-then-agent': {
        title: 'La lista es CompliCore. La revisión diaria es CompliAdvantage.',
        body:
          'Archive la prueba de cada paso usted mismo. La revisión diaria escribe hallazgos. No cambia la cuenta y no marca un paso como hecho. Un recurso que la revisión no puede ver no es un pase.',
      },
    },
    pillars: {
      'connect-cloud': {
        label: 'Conectar la cuenta del cliente',
        summary: 'Nombre la cuenta del cliente y registre el acceso de solo lectura que concedió.',
        items: {
          'cloud-compliance-calendar': item(
            'Ponga las fechas de este trabajo en el calendario',
            'Abra el calendario de esta página y guarde una fecha para la revisión de solo lectura y la rotación de claves. Elija No este año solo cuando esa fila no aplique. No invente una clave ni una fecha.',
            'Cada fecha que aplica, guardada en el calendario',
            {
              completeLabel: 'Marcar completo (fechas en el calendario)',
              verifySteps: {
                'cloud-review': {
                  text: 'Guarde la fecha de la revisión de nube de solo lectura, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
                'key-rotation': {
                  text: 'Guarde la fecha de rotación de claves, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
              },
            },
          ),
          'name-client-cloud': item(
            'Nombrar la cuenta de nube del cliente',
            'Registre el nombre de la cuenta del cliente y la nube donde corre. Un cliente, una cuenta. No reutilice la cuenta de operaciones de NextCompli.',
            'Nombre de la cuenta y la nube, guardados en el archivo de la empresa',
          ),
          'grant-read-only': item(
            'Registrar el acceso de solo lectura que concedió el cliente',
            'Pida al cliente acceso de solo lectura a esa cuenta. En AWS, un rol de lectura administrado por el cliente en el que confía solo el revisor, con un id externo. En Azure, Reader y Security Reader. En Google Cloud, Security Reviewer, no Owner. Guarde quién lo concedió y la fecha. No guarde contraseñas ni claves secretas en esta lista.',
            'Quién concedió el acceso, y la fecha',
            { dependencyNote: 'Después de nombrar la cuenta.' },
          ),
          'review-boundary': item(
            'Confirmar qué puede ver el acceso',
            'Escriba qué recursos puede ver el acceso de solo lectura y cuáles no. Una revisión que no puede ver un recurso no es un pase para ese recurso.',
            'Lista de lo que el acceso puede y no puede ver',
            { dependencyNote: 'Después de registrar el acceso.' },
          ),
        },
      },
      'identity-access': {
        label: 'Inicio de sesión y permisos',
        summary: 'Solo quien lo necesita puede cambiar la cuenta, y entra con un segundo factor.',
        items: {
          'mfa-privileged': item(
            'MFA en cada administrador',
            'Cada persona que puede cambiar la cuenta entra con MFA. En AWS incluye el usuario raíz y cada usuario de consola. En Azure incluye cada rol privilegiado. En Google Cloud incluye cada cuenta que puede cambiar el proyecto. Una contraseña sola no basta.',
            'Fecha en que se confirmó MFA para los administradores',
          ),
          'root-unused': item(
            'El dueño de la cuenta no se usa para el trabajo diario',
            'El usuario raíz de AWS, el dueño de la cuenta de Azure y el dueño de la organización de Google Cloud no se usan para cambios cotidianos. El trabajo diario usa una persona con un rol más pequeño. Archive la fecha del último inicio de sesión del dueño.',
            'Fecha del último inicio de sesión del dueño, y quién hace el trabajo diario',
          ),
          'no-long-lived-keys': item(
            'Las personas no guardan claves de acceso de larga vida',
            'Las personas entran como ellas mismas. Las cargas de trabajo usan un rol, una identidad administrada o una cuenta de servicio. Toda clave de acceso de más de 90 días se quita o se reemplaza. No pegue una clave en esta lista.',
            'Cantidad de claves de usuario de más de 90 días',
          ),
          'least-privilege-review': item(
            'Revisar quién puede cambiar la cuenta',
            'Al menos una vez al año, liste quién puede administrar la cuenta y quite a quien ya no lo necesita. En AWS mire usuarios, grupos y roles. En Azure mire asignaciones de roles. En Google Cloud mire los vínculos del proyecto y de la organización.',
            'Fecha de la revisión y la lista de administradores',
          ),
          'admin-sign-in-restricted': item(
            'El inicio de sesión de administrador no está abierto a todo internet',
            'La administración de la consola y de la API se limita a la red de la empresa, un proveedor de identidad conocido o un punto de conexión privado. Una política de contraseñas exige longitud y rotación donde la nube todavía usa contraseñas.',
            'Cómo está limitado el inicio de sesión de administrador',
          ),
        },
      },
      'audit-logging': {
        label: 'Registro de auditoría',
        summary: 'Los cambios de administración se registran, se conservan y disparan una alarma.',
        items: {
          'audit-log-on': item(
            'Un registro de auditoría anota los cambios de administración',
            'Active un registro de cambios de administración en cada región que usa la cuenta. En AWS es CloudTrail. En Azure es el registro de actividad. En Google Cloud son los registros de auditoría, incluido el acceso a datos cuando la carga maneja datos del cliente.',
            'Dónde está activo el registro, y las regiones que cubre',
          ),
          'audit-log-retained': item(
            'Los registros de auditoría se conservan y se protegen',
            'Conserve el registro al menos un año. Las personas que el registro describe no pueden borrarlo. En AWS, la validación del archivo está activa y el depósito bloquea el acceso público. En Azure, el área de registro está bloqueada. En Google Cloud, el depósito de registros se conserva y no es público.',
            'Periodo de conservación y dónde se guarda el registro',
          ),
          'audit-alarms': item(
            'Una alarma se dispara cuando cambian el registro o el inicio de sesión',
            'Una alarma llega a una persona cuando alguien apaga el registro, cambia la red o cambia quién puede entrar. Un registro en silencio no es un control.',
            'Nombre de la alarma y quién la recibe',
          ),
        },
      },
      'network-exposure': {
        label: 'Exposición de la red',
        summary: 'Los puertos de administración y las redes predeterminadas no quedan abiertos.',
        items: {
          'no-open-admin-ports': item(
            'Los puertos de administración remota no están abiertos a todo internet',
            'Los puertos 22 y 3389 no están abiertos a 0.0.0.0/0 ni a ::/0. La administración usa una red privada, un bastión con MFA o un proxy que conoce la identidad.',
            'La regla que todavía permite esos puertos, o una nota de que ninguna lo hace',
          ),
          'flow-logs': item(
            'Los registros de flujo de red están activos',
            'Los registros de flujo están activos en las redes que alojan la carga. En AWS son los registros de flujo de VPC. En Azure es Network Watcher. En Google Cloud son los registros de flujo de VPC.',
            'Redes con registros de flujo, y dónde caen esos registros',
          ),
          'default-network-closed': item(
            'La red predeterminada no queda abierta',
            'La red predeterminada de la nube, el grupo de seguridad predeterminado y el almacenamiento predeterminado no se usan para la carga, o están cerrados para no aceptar tráfico público.',
            'Qué se hizo con la red predeterminada y el almacenamiento predeterminado',
          ),
        },
      },
      'data-protection': {
        label: 'Protección de datos',
        summary: 'El almacenamiento está cifrado, es privado, tiene copia y se puede restaurar.',
        items: {
          'encryption-at-rest': item(
            'El almacenamiento y las bases cifran los datos en reposo',
            'El almacenamiento de objetos, los discos y las bases cifran los datos en reposo. Anote si la clave es la predeterminada de la nube o una clave del cliente, y quién puede usarla.',
            'Qué está cifrado, y quién controla la clave',
          ),
          'no-public-storage': item(
            'El almacenamiento no es público',
            'Los depósitos, blobs y discos no son públicos. El bloqueo de acceso público está activo. Un archivo que debe ser público tiene nombre, dueño y revisión.',
            'Ajuste de acceso público, y cualquier excepción con un dueño',
          ),
          'storage-in-transit': item(
            'El almacenamiento exige una conexión cifrada',
            'Los clientes llegan al almacenamiento por TLS. En AWS, el depósito niega las solicitudes que no son seguras. En Azure, se exige la transferencia segura. En Google Cloud, el depósito no se sirve por HTTP simple.',
            'Ajuste que rechaza una conexión simple',
          ),
          'backups-restore-tested': item(
            'Hay copias y se probó una restauración',
            'Las bases y los datos que el negocio no puede perder tienen una copia. Se probó una restauración en el último año. Una copia que nunca se restauró no es prueba.',
            'Calendario de copias y la fecha de la última prueba de restauración',
          ),
          'key-rotation': item(
            'Las claves del cliente rotan',
            'Si el cliente usa su propia clave de cifrado, rota según un calendario y la clave anterior se retira. Anote la fecha de la última rotación.',
            'Nombre de la clave y fecha de la última rotación, o una nota de que se usa la clave predeterminada de la nube',
          ),
        },
      },
      'findings-inventory': {
        label: 'Hallazgos e inventario',
        summary: 'Un servicio de hallazgos está activo, cada hallazgo crítico tiene dueño, y el inventario coincide con la cuenta.',
        items: {
          'findings-service-on': item(
            'Un servicio de hallazgos está activo',
            'Active el servicio de hallazgos de la nube para esta cuenta. En AWS es Security Hub. En Azure es Microsoft Defender for Cloud. En Google Cloud es Security Command Center. Ahí comparan los operadores con el CIS Foundations Benchmark de esa nube. NextCompli no copia esos textos.',
            'Nombre del servicio y la fecha de la última revisión',
          ),
          'critical-finding-owner': item(
            'Cada hallazgo crítico tiene dueño y fecha',
            'Cada hallazgo crítico o alto tiene una persona y una fecha, o una razón escrita de por qué se acepta. Una página de hallazgos sin leer no es un pase.',
            'Hallazgos críticos abiertos, cada uno con un dueño',
          ),
          'inventory-current': item(
            'El inventario coincide con la cuenta',
            'Existe una lista actual de cuentas, proyectos y suscripciones. Un recurso en una región o un proyecto que la revisión no puede ver se nombra como no visto, no como aprobado.',
            'Fecha del inventario y cualquier cuenta que la revisión no puede ver',
          ),
        },
      },
      'cloud-agent': {
        label: 'Revisión diaria de solo lectura',
        summary: 'CompliAdvantage. El agente mira. Una persona decide.',
        items: {
          'record-readonly-role': item(
            'Registrar el rol de solo lectura',
            'Guarde el nombre del rol que concedió el cliente. AWS: un rol de lectura en el que confía solo el revisor, con un id externo, no una clave guardada. Azure: Reader y Security Reader. Google Cloud: Security Reviewer. No pegue una contraseña ni una clave secreta.',
            'Nombre del rol, la nube y la fecha en que se concedió',
            { dependencyNote: 'Después de registrar el acceso.' },
          ),
          'daily-readonly-review': item(
            'Poner en cola la revisión de solo lectura una vez al día',
            'Ponga en cola una revisión al día para ese rol. La revisión lee identidad, registro, red, almacenamiento, cifrado, copias y hallazgos. No cambia la cuenta. No usa la cuenta de operaciones de NextCompli. No marca estos pasos como hechos.',
            'Fecha de la última revisión en cola',
            { dependencyNote: 'Después de registrar el rol.' },
          ),
          'person-reviews-findings': item(
            'Una persona lee los hallazgos del día',
            'Una persona lee lo que encontró la revisión y archiva la prueba en el paso de CompliCore que corresponde. Lo incierto y lo no visto siguen abiertos. El agente no envía un cambio a la nube.',
            'Quién revisó los hallazgos, y la fecha',
            { dependencyNote: 'Después de poner la revisión en cola.' },
          ),
        },
      },
    },
  },
  'ofac-compliance': {
    title: 'Cumplimiento OFAC',
    subtitle:
      'Una lista para el cribado de sanciones. Actualice las listas SDN y CAPTA, importe nombres, ejecute Screen now y decida cada posible coincidencia. Las investigaciones y la lista blanca esperan esa decisión. Esta página no sustituye las herramientas de cribado.',
    notices: {
      'ofac-not-counsel': {
        title: 'Una lista, no una opinión de sanciones',
        body: 'NextCompli criba nombres contra listas OFAC y guarda la decisión. No le dice que un pago, un envío o una contratación sea lícito. Consulte a un abogado cuando la coincidencia sea real.',
      },
      'ofac-tools-stay': {
        title: 'Las herramientas quedan a un clic de la tarea',
        body: 'Screening, las listas CAPTA, Investigaciones, la lista blanca y el feed en vivo son donde se hace el trabajo. Esta página muestra qué está hecho, qué está bloqueado y qué sigue abierto.',
      },
    },
    pillars: {
      'ofac-lists': {
        label: 'Cobertura de listas',
        summary: 'Confirme que la lista SDN y la lista CAPTA se actualizaron antes de cribar cualquier nombre.',
        items: {
          'ofac-compliance-calendar': item(
            'Ponga las fechas de este trabajo en el calendario',
            'Abra el calendario de esta página y guarde la fecha de la próxima revisión de las listas SDN y CAPTA. No invente una coincidencia.',
            'Fecha guardada en el calendario',
            {
              completeLabel: 'Marcar completo (fechas en el calendario)',
              verifySteps: {
                'list-refresh': {
                  text: 'Guarde la fecha de la próxima revisión de las listas SDN y CAPTA, o márquela como no este año.',
                  evidenceHint: 'Fecha en el calendario de cumplimiento',
                },
              },
            },
          ),
          'ofac-refresh-lists': item(
            'Actualizar las listas SDN y CAPTA',
            'Abra el feed en vivo y confirme que la lista SDN y la lista CAPTA muestran una fecha de actualización correcta. El cribado espera esa fecha.',
            'Fecha de actualización de la lista SDN y de la lista CAPTA',
            { sourceLabel: 'Abrir feed en vivo', docsLabel: 'Abrir listas CAPTA' },
          ),
        },
      },
      'ofac-screening': {
        label: 'Cribado de nombres',
        summary: 'Importe los nombres de esta cuenta y luego ejecute Screen now.',
        items: {
          'ofac-import-subjects': item(
            'Importar los nombres a cribar',
            'En Screening, importe las personas y empresas que esta cuenta debe revisar. Use el nombre legal. Deje en blanco las fechas y direcciones que no conozca.',
            'Cantidad de sujetos importados en Screening',
            { dependencyNote: 'Primero van las fechas de actualización de SDN y CAPTA.', sourceLabel: 'Abrir Screening' },
          ),
          'ofac-screen-now': item(
            'Ejecutar Screen now',
            'En Screening, ejecute Screen now para los nombres importados. Lea cada resultado antes de descartar un nombre.',
            'Screen now terminado para esta cuenta',
            { dependencyNote: 'Importe los nombres antes de Screen now.', sourceLabel: 'Abrir Screening' },
          ),
        },
      },
      'ofac-review': {
        label: 'Cola de revisión',
        summary: 'Decida cada posible coincidencia antes de una investigación o de una entrada en la lista blanca.',
        items: {
          'ofac-review-matches': item(
            'Decidir cada posible coincidencia',
            'En Screening, descarte cada posible coincidencia con un motivo escrito, o escálela. Una posible coincidencia abierta sigue bloqueada.',
            'Nota de decisión para cada posible coincidencia',
            { dependencyNote: 'Screen now tiene que terminar antes de decidir la cola.', sourceLabel: 'Abrir Screening' },
          ),
        },
      },
      'ofac-investigations': {
        label: 'Investigaciones',
        summary: 'Aplique la regla del 50% cuando una revisión se escala.',
        items: {
          'ofac-investigation': item(
            'Investigar una coincidencia escalada',
            'Cuando una revisión se escala, abra Investigaciones y aplique la regla del 50% a dueños y empresas relacionadas. Registre la decisión en el caso. Omita este paso cuando nada se escaló y anótelo.',
            'Decisión de la investigación, o una nota de que nada se escaló',
            { dependencyNote: 'La decisión de la revisión va primero.', sourceLabel: 'Abrir Investigaciones' },
          ),
        },
      },
      'ofac-whitelist': {
        label: 'Lista blanca',
        summary: 'Registre un falso positivo confirmado después de la revisión.',
        items: {
          'ofac-whitelist-false-positive': item(
            'Poner en la lista blanca un falso positivo confirmado',
            'Cuando una revisión confirma un falso positivo, agregue ese nombre en la lista blanca con el motivo y la fecha. Omita este paso cuando no hay falso positivo y anótelo. El siguiente cribado igual se ejecuta.',
            'Entrada en la lista blanca con motivo y fecha, o una nota de que no aplicó',
            { dependencyNote: 'La revisión tiene que confirmar el falso positivo primero.', sourceLabel: 'Abrir lista blanca' },
          ),
        },
      },
      'ofac-live-feed': {
        label: 'Feed en vivo',
        summary: 'Mantenga las fuentes oficiales de OFAC en un calendario después de que las listas se actualicen.',
        items: {
          'ofac-live-feed-schedule': item(
            'Programar el feed oficial de OFAC',
            'En el feed en vivo, mantenga las fuentes oficiales de OFAC en un calendario para que la siguiente actualización de SDN y CAPTA no sea una revisión única. CompliAdvantage incluye este paso.',
            'Calendario del feed en vivo, o la hora de la última actualización automática',
            { dependencyNote: 'Confirme una actualización correcta de SDN y CAPTA antes de confiar en el calendario.', sourceLabel: 'Abrir feed en vivo' },
          ),
        },
      },
    },
  },
};
